import {
  CaretRightOutlined,
  ControlOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  PlusOutlined,
  SaveOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  ColorPicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Slider,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { customElementsApi } from '@/api/customElements';
import {
  DEFAULT_TEXT_MARGIN_PX,
  type FileEntryCondition,
  isLayoutBlockType,
  type WorkflowField,
  type WorkflowFieldPropertiesPayload,
  type WorkflowFieldType,
  type WorkflowStep,
  workflowsApi,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { OPTION_ICONS } from '@/form-renderer/optionIcons';
import { handleApiError } from '@/lib/handleApiError';

import { ConditionsEditorModal } from '../conditions/ConditionsEditorModal';
import { hasEligibleSourceFields } from '../conditions/sourceFields';

import { SaveAsCustomElementModal } from './customElements/SaveAsCustomElementModal';
import { fieldDisplayLabelKey, metaForFieldType } from './fieldMeta';

const { Text } = Typography;

/**
 * Context exposing the panel-level "kick the debounced auto-save" function so
 * helper components that mutate the form programmatically (Help modals via
 * `setFieldsValue`, the per-option Option-Behaviour popover via
 * `setFieldValue`) can trigger the same persist pipeline that `onValuesChange`
 * triggers for keystroke edits. AntD's `setFieldValue` / `setFieldsValue`
 * deliberately bypasses `onValuesChange`, so without this nudge the
 * programmatic writes vanish on refresh — exactly the help-bubble "I added it
 * and refresh and it's gone" bug.
 *
 * Default is a no-op so components rendered outside the panel (or in tests)
 * stay safe. The real callback is supplied via the Provider inside `FieldEditor`.
 */
const AutoSaveTriggerContext = createContext<() => void>(() => {});
const useTriggerAutoSave = () => useContext(AutoSaveTriggerContext);

type FormValues = {
  labelEn: string;
  labelFr?: string;
  required: boolean;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  integerOnly?: boolean;
  options?: OptionRow[];
  helpTitleEn?: string;
  helpTitleFr?: string;
  helpBodyEn?: string;
  helpBodyFr?: string;
  hintEn?: string;
  hintFr?: string;
  // Required-error message override (only meaningful when required = true).
  requiredMessageEn?: string;
  requiredMessageFr?: string;
  fontSize?: number;
  color?: string;
  marginTop?: number;
  marginBottom?: number;
  listStyle?: string;
  fontWeight?: string;
  inputRestriction?: string;
  backgroundColor?: string;
  icon?: string;
  validate?: string;
  // Regex pattern + bilingual error message — only meaningful when
  // validate === 'regex'. Kept mounted (hidden when another rule is picked)
  // so the panel's live-values save doesn't drop them mid-edit.
  regexPattern?: string;
  regexMessageEn?: string;
  regexMessageFr?: string;
  // 'canada-post' enables the AddressComplete autocomplete on ShortText.
  // The properties panel surfaces it as a single checkbox; the form value
  // is the string 'canada-post' when enabled, undefined when off.
  addressAutocomplete?: string;
  // Role this field plays in an AddressComplete sibling group. The
  // runtime fills siblings by role within the trigger field's section.
  // Allow-list mirrored from the BE: street / city / province / postal / apt.
  addressRole?: string;
  minAge?: number;
  // Number-only display format. 'currency' = "$" prefix + thousand commas;
  // 'percentage' = "%" suffix. Empty string = plain numeric input.
  numberFormat?: string;
  // PDF AcroForm mapping handle. Sent on every input field's payload.
  internalName?: string;
  // RadioGroup / SingleSelect / CheckboxGroup — option values that disable
  // Next when the advisor's answer matches. Empty = no blocking.
  blockingValues?: string[];
  // CheckboxGroup only — how blockingValues combine. 'or' (default) blocks on
  // any listed value; 'and' blocks only when all are selected.
  blockingValueOperator?: 'and' | 'or';
  // Block-continue decorations — only meaningful when blockingValues is set.
  // Alert switch pops a modal in preview each time a blocking answer is
  // selected; the bilingual message shows under the field / in the modal /
  // as the disabled-Next tooltip.
  blockContinueAlert?: boolean;
  blockContinueMessageEn?: string;
  blockContinueMessageFr?: string;
  // RadioGroup / CheckboxGroup only — stack the options vertically (one per
  // line) instead of the default horizontal flow.
  verticalOptions?: boolean;
  // RadioGroup / CheckboxGroup only — render options as bordered cards with
  // an optional per-option icon instead of a control-and-label list. Panel-
  // local boolean; persisted on the shared `displayAs` property as
  // 'card' | 'list'.
  optionCardLayout?: boolean;
  // Repeatable row column template — only meaningful on RepeatableRow.
  repeatableTemplate?: RepeatableTemplateRow[];
  // RepeatableRow advisor-side row cap. Null/undefined = unlimited.
  maxRows?: number;
  anchorFirstRow?: boolean;
  // Image-only — uploaded image URL + width / alignment presets + bilingual
  // caption + alt text. ImageUrl lives on the field after upload; the panel
  // streams the picked file through workflowsApi.uploadHelpImage (same
  // general-purpose endpoint as the help-bubble image attachments).
  imageUrl?: string;
  imageWidthSize?: string;
  imageAlignment?: string;
  captionEn?: string;
  captionFr?: string;
  altEn?: string;
  altFr?: string;
  // When true, the renderer disables the input and skips required-empty check.
  // BE refuses on layout blocks; the panel hides the row for those types.
  readOnly?: boolean;
  // Advisor-session prefill key. BE allow-list mirrors the FormShell resolver;
  // panel exposes the same options via a Select. Blank = no prefill.
  sourceValue?: string;
  // Cross-row sum rule — RepeatableRow only. Authored via the editor at the
  // bottom of RepeatableTemplateEditor. `columnKey` picks which template
  // column is summed; `mode` picks whether the target is an absolute
  // constant or another field's live value.
  //
  // `sumValidationEnabled` is a form-side-only master toggle: when off the
  // config controls are hidden and buildSumValidation collapses the payload
  // to null regardless of what the fields hold. Hydrated on from the
  // presence of a persisted rule.
  sumValidationEnabled?: boolean;
  sumValidation?: {
    columnKey?: string;
    mode?: 'constant' | 'field';
    targetConstant?: number;
    targetFieldId?: string;
    // Bilingual caption describing what the total represents, shown next to
    // the live sum in the renderer in place of the generic "Current sum".
    labelEn?: string;
    labelFr?: string;
  };
  // Cross-field uniqueness tag — free-form string, ShortText only. Any two
  // fields carrying the same non-empty group name become peers whose values
  // must be pairwise distinct. Blank = not in a group.
  uniquenessGroup?: string;
  minSelections?: number;
  // Cross-step prefill source. The picker below writes `stepId:fieldId`
  // to sourceFieldStepId + sourceFieldFieldId when a source is chosen;
  // clearing either clears both. Split into two form-side keys so AntD
  // cascader semantics don't need a nested Form.Item.
  sourceFieldStepId?: string;
  sourceFieldFieldId?: string;
};

/** A single row in the repeatable-template editor. Mirrors the FE
 *  RepeatableColumn type but with `labelFr` optional to match the form's
 *  per-row controlled inputs. Every per-column property the BE accepts is
 *  carried through verbatim — the editor surfaces only `label / type /
 *  required / maxLength` in its UI today, but the seed / API can author
 *  richer columns (options, validate, inputRestriction, placeholder,
 *  internalName) and edit/save must not silently strip those.
 */
type RepeatableTemplateRow = {
  id: string;
  type: WorkflowFieldType;
  labelEn: string;
  labelFr?: string;
  required: boolean;
  maxLength?: number;
  placeholder?: string;
  options?: OptionRow[];
  validate?: string;
  inputRestriction?: string;
  internalName?: string;
  widthPercent?: number;
  helpEn?: string;
  helpFr?: string;
  /** Form-side master switch for the dual-unit block below — mirrors the
   *  `sumValidationEnabled` pattern. Off means no toggle is authored at all. */
  unitToggleEnabled?: boolean;
  unitToggle?: {
    defaultMode?: 'dollar' | 'percent';
    dollarTargetConstant?: number | null;
    percentTargetConstant?: number | null;
  };
};

type OptionRow = {
  value: string;
  // French PDF value. Always mirrors `value` — the "French value same as
  // English" switch was removed 2026-07-25 because the two are never
  // authored apart. Shown read-only so the originator can still see which
  // token the FR row carries.
  valueFr?: string | null;
  labelEn: string;
  labelFr?: string;
  // Per-item help bubble (currently surfaced by BulletedList rows). EN+FR must
  // be paired (both halves set, or neither) — BE refuses an unpaired set, so
  // buildProperties pre-clears any half-filled bag before sending.
  helpTitleEn?: string | null;
  helpTitleFr?: string | null;
  helpBodyEn?: string | null;
  helpBodyFr?: string | null;
  // CheckboxGroup-only — other option values on the same group that get cleared
  // at runtime when this option is checked. Surfaced in the per-row Option
  // behaviour modal; null/empty means "no rule".
  clearsOnSelect?: string[] | null;
  // RadioGroup / CheckboxGroup only — id of another field on the SAME step that
  // this option borrows for inline display when selected. Surfaced as a Select
  // in the per-row Option behaviour modal. Null = no borrow.
  revealFieldId?: string | null;
  // Per-option AcroForm field name — Radio / SingleSelect / CheckboxGroup only.
  // Points at a boolean field in the source PDF that the runtime CHECKS when
  // this option is selected/checked at fill time. Blank = no PDF mapping.
  internalName?: string | null;
  // RadioGroup / CheckboxGroup only — OPTION_ICONS registry key for the icon
  // shown on this option's card when the field uses the card layout. Blank /
  // null = no icon.
  icon?: string | null;
};

// Layout blocks (StaticText / Paragraph / Divider / BulletedList / Notice)
// re-purpose the Label field as the rendered content. They have no required
// flag (non-input), no placeholder / length / numeric properties, and no
// help bubble / hint (the BE refuses both on these types). Identified
// throughout this file by `isLayoutBlockType(field.type)` — the single
// source of truth lives on workflows.ts so the form-renderer's
// step-completion calc can't drift from this gating list.

type Props = {
  field: WorkflowField | null;
  /** Owning workflow template. Threaded down to the help modal so its image-upload
   *  button can hit `POST /api/v1/workflows/{templateId}/help-images`. */
  templateId: string | undefined;
  /** Owning workflow step. Needed by the visibility-conditions endpoint URL. */
  stepId: string | undefined;
  /** Selected (section, row, column) — same triple the canvas uses. */
  location: { sectionId: string; rowId: string; columnId: string } | null;
  /** Every step in the workflow — passed to the visibility modal so a field's
   *  condition can reference any prior step's source field. */
  allSteps: WorkflowStep[];
  onSave: (payload: {
    labelEn: string;
    labelFr?: string | null;
    required: boolean;
    properties: WorkflowFieldPropertiesPayload;
  }) => Promise<void>;
  onRemove: () => Promise<void>;
  /** Fired after a successful visibility-conditions PUT so the parent can
   *  refetch and the panel reflects the new conditions[] on the field. */
  onConditionsChanged: () => Promise<void>;
  /** Fired when the user clicks the X. The page clears its selection so the panel
   *  unmounts; clicking another placed field re-opens it. */
  onClose: () => void;
  /**
   * Client-side conditions handler for the custom element editors (no real
   * templateId / workflow API). When provided and templateId is absent, called
   * instead of workflowsApi.setFieldConditions so the in-memory sections state
   * gets updated directly.
   */
  onSaveFieldConditions?: (
    sectionId: string,
    rowId: string,
    columnId: string,
    conditions: FileEntryCondition[],
    combinator: 'all' | 'any' | null,
  ) => Promise<void>;
  /**
   * Hides the "Save as custom field" affordance. The standalone custom-Field
   * editor sets this — you're already editing a saved custom field there, so
   * re-saving it as a (duplicate) custom field is meaningless.
   */
  hideSaveAsCustom?: boolean;
};

// Compact / expanded panel widths. The expand toggle in the panel header
// flips between the two so options-heavy types (BulletedList, SingleSelect,
// RadioGroup, CheckboxGroup) can give EN+FR cells real room without forcing
// every panel to default wide.
const RAIL_WIDTH_COMPACT = 320;
const RAIL_WIDTH_EXPANDED = 600;
const RAIL_BASE: CSSProperties = {
  flexShrink: 0,
  borderLeft: '1px solid #e5e7eb',
  background: '#fff',
  padding: 18,
  overflowY: 'auto',
  transition: 'width 0.18s ease',
};
// Persist the originator's expanded preference across mounts + sessions so
// the panel doesn't keep snapping back to compact every time a field is
// reselected.
const PANEL_EXPANDED_KEY = 'bmo-inflow.builder.propertiesPanelExpanded';

// AntD Form.Item defaults to 24px bottom margin. Tighten it across the panel so
// the properties list reads as a cohesive form, not a stack of widely-spaced
// labels. The Save / Delete row carries its own marginTop so this doesn't
// affect the buttons.
const COMPACT_ITEM: CSSProperties = { marginBottom: 10 };

// Per-option action buttons (item help / Option behaviour / delete). They
// stack vertically in one 24px grid column that spans the EN + FR rows, so
// each has to be exactly square and padding-free — AntD's default small-button
// horizontal padding would blow the column out.
const OPTION_ACTION_BTN: CSSProperties = {
  width: 24,
  height: 24,
  minWidth: 24,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Tick labels on the margin sliders — discrete 0 / 5 / 10 / 15 / 20 / 25 / 30 px.
// Hoisted so the AntD Slider's marks prop is a stable reference and the
// component doesn't reinitialize on every panel render.
const MARGIN_MARKS: Record<number, string> = {
  0: '0',
  5: '5',
  10: '10',
  15: '15',
  20: '20',
  25: '25',
  30: '30',
};

// Header button for a CollapsibleSection — full-width, borderless, chevron +
// uppercase-ish section title. Mirrors the FieldPalette category header so the
// two collapsible surfaces in the builder read as one system.
const SECTION_HEADER_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  // Native <button> doesn't inherit the page font — the app sets Heebo on
  // <body> but has no global button reset, so without this the section headers
  // would render in the browser's default font instead of matching the app.
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  color: '#374151',
  textAlign: 'left',
};

const SECTION_KEY_PREFIX = 'bmo-inflow.builder.propsSection.';

/**
 * A titled, collapsible group of properties inside the FieldEditor rail.
 *
 * Critical: children are ALWAYS mounted and merely hidden with `display: none`
 * when collapsed — never conditionally rendered. The panel's auto-save builds
 * its payload from live form values (`form.validateFields()` → buildProperties),
 * and an unmounted `Form.Item` deregisters from the form store, so its value
 * would silently vanish on the next save. Hiding (not unmounting) keeps every
 * slot — including the hidden help-field inputs — registered and persisting.
 *
 * Open/closed state is remembered per `id` in localStorage (same try/catch
 * pattern as the panel-width preference) so a section stays how the originator
 * left it across field selections and reloads. `defaultOpen` seeds the very
 * first view before any preference exists.
 */
function CollapsibleSection({
  id,
  title,
  hint,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  /** Optional descriptor rendered directly under the header (used by the PDF
   *  mapping section). Stays visible whether the section is open or closed. */
  hint?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const storageKey = `${SECTION_KEY_PREFIX}${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored === null ? defaultOpen : stored === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* ignore quota / private-mode */
      }
      return next;
    });
  };
  return (
    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginBottom: 12 }}>
      <button type="button" style={SECTION_HEADER_BTN} onClick={toggle} aria-expanded={open}>
        <CaretRightOutlined
          style={{
            fontSize: 10,
            transition: 'transform 140ms ease',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
        {title}
      </button>
      {hint && <div style={{ fontSize: 11, color: '#6b7280', margin: '6px 0 2px' }}>{hint}</div>}
      <div style={{ display: open ? 'block' : 'none', marginTop: 10 }}>{children}</div>
    </div>
  );
}

/**
 * Three-row group for a single option inside OptionsEditor:
 *   Row 1 (EN): EN tag + EN value + EN label + action buttons (help / exclusion / delete)
 *   Row 2 (FR): FR tag + FR value (read-only mirror of EN) + FR label
 *
 * Kept as a separate component so it can call useWatch / useEffect per-row
 * without violating the rules-of-hooks constraint inside a .map() callback.
 */
function OptionGroup({
  name,
  hideValue,
  supportItemHelp,
  supportExclusion,
  supportReveal,
  supportOptionInternalName,
  supportOptionIcon,
  onHelpEdit,
  onBehaviourEdit,
  onRemove,
  gridCols,
}: {
  name: number;
  hideValue: boolean;
  supportItemHelp: boolean;
  supportExclusion: boolean;
  supportReveal?: boolean;
  /** When true, adds a fourth sub-row per option with an inline "PDF field"
   *  input mapping the option value to an AcroForm checkbox. Only on for
   *  Radio / SingleSelect / CheckboxGroup at the OptionsEditor caller. */
  supportOptionInternalName?: boolean;
  /** Adds a card-icon picker per option. RadioGroup / CheckboxGroup only —
   *  the two types whose options can render as cards. */
  supportOptionIcon?: boolean;
  onHelpEdit: () => void;
  onBehaviourEdit: () => void;
  onRemove: () => void;
  gridCols: string;
}) {
  const { t, i18n } = useTranslation();
  // Icon picker labels are originator-facing chrome, so they follow the
  // builder's own locale rather than the workflow's authored language.
  const isFr = i18n.language.startsWith('fr');
  const form = Form.useFormInstance();
  const triggerAutoSave = useTriggerAutoSave();

  // Watch per-row state so the FR value input stays in sync and the help icon
  // re-colours without needing the parent to re-render.
  const enValue = Form.useWatch(['options', name, 'value']) as string | undefined;
  const helpTitleEn = Form.useWatch(['options', name, 'helpTitleEn']) as string | undefined;
  const helpBodyEn = Form.useWatch(['options', name, 'helpBodyEn']) as string | undefined;

  // Mirror EN value → FR value. Unconditional now that the "same as English"
  // switch is gone; the FR input is read-only, so this is the only writer.
  useEffect(() => {
    form.setFieldValue(['options', name, 'valueFr'], enValue ?? '');
  }, [enValue, form, name]);

  const hasItemHelp = Boolean(helpTitleEn?.trim() && helpBodyEn?.trim());

  const langTagStyle: CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    userSelect: 'none',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* EN + FR share ONE grid so the action buttons can live in a single
          narrow column that spans both language rows, stacked vertically.
          Two grids side by side would force the buttons to sit in a row next
          to the EN input and leave dead space beside the FR one. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          columnGap: 6,
          rowGap: 3,
          alignItems: 'center',
        }}
      >
        {/* EN Row */}
        <span style={langTagStyle}>EN</span>
        {!hideValue && (
          <Form.Item name={[name, 'value']} rules={[{ required: true, message: '' }]} noStyle>
            <Input size="small" placeholder="PDF value" />
          </Form.Item>
        )}
        <Form.Item name={[name, 'labelEn']} rules={[{ required: true, message: '' }]} noStyle>
          <Input size="small" placeholder="EN" />
        </Form.Item>
        {/* Actions column — spans both language rows. Auto-placement puts this
            at row 1 of the last column and the FR cells below wrap around it. */}
        <div
          style={{
            gridRow: 'span 2',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {supportItemHelp && (
            <Tooltip
              title={
                hasItemHelp
                  ? t('workflow.builder.properties.editItemHelp')
                  : t('workflow.builder.properties.addItemHelp')
              }
            >
              <Button
                type="text"
                size="small"
                style={OPTION_ACTION_BTN}
                icon={
                  <InfoCircleOutlined
                    style={{ fontSize: 14, color: hasItemHelp ? '#0179C1' : '#6b7280' }}
                  />
                }
                onClick={onHelpEdit}
                aria-label={t('workflow.builder.properties.editItemHelp')}
              />
            </Tooltip>
          )}
          {(supportExclusion || supportReveal) && (
            <OptionBehaviourButton rowIndex={name} onEdit={onBehaviourEdit} />
          )}
          <Button
            type="text"
            size="small"
            danger
            style={OPTION_ACTION_BTN}
            icon={<DeleteOutlined style={{ fontSize: 12 }} />}
            onClick={onRemove}
            aria-label={t('workflow.builder.properties.removeOption')}
          />
        </div>

        {/* FR Row */}
        <span style={langTagStyle}>FR</span>
        {/* Read-only mirror of the EN value — kept visible so the FR row shows
            which PDF token it carries, but not editable since the two are
            always the same. */}
        {!hideValue && (
          <Form.Item name={[name, 'valueFr']} noStyle>
            <Input size="small" placeholder="value" disabled />
          </Form.Item>
        )}
        <Form.Item name={[name, 'labelFr']} noStyle>
          <Input size="small" placeholder="FR" />
        </Form.Item>
      </div>

      {/* Per-option AcroForm checkbox mapping is now assigned visually (and
          1-to-many, per PDF) in the PDF Field Mapping tool. Read-only hint here
          so the originator knows where to map; the BE preserves each option's
          mappings across property edits. */}
      {supportOptionInternalName && (
        <div style={{ marginLeft: 34, fontSize: 11, color: '#6b7280' }}>
          {t('workflow.builder.properties.optionInternalNameHintReadonly')}
        </div>
      )}
      {/* Card icon picker — only offered on the card-capable types. Shown
          regardless of whether the card layout switch is currently on, so an
          author can set icons before flipping the layout (the BE validates
          on field TYPE, not on displayAs, for exactly that reason). */}
      {supportOptionIcon && (
        <div
          style={{
            marginLeft: 34,
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
            {t('workflow.builder.properties.optionIcon')}
          </span>
          <Form.Item name={[name, 'icon']} noStyle>
            <Select
              size="small"
              allowClear
              style={{ minWidth: 160 }}
              placeholder={t('workflow.builder.properties.optionIconNone')}
              onChange={triggerAutoSave}
              options={OPTION_ICONS.map((i) => ({
                value: i.name,
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <img src={i.src} alt="" aria-hidden style={{ width: 16, height: 16 }} />
                    {isFr ? i.labelFr : i.labelEn}
                  </span>
                ),
              }))}
            />
          </Form.Item>
        </div>
      )}
    </div>
  );
}

/**
 * Structured editor for the SingleSelect / RadioGroup / CheckboxGroup / BulletedList
 * options list. Each option renders three rows (EN, FR, and a "same value" switch)
 * via OptionGroup. "+ Add option" appends a blank row. The form values flow as
 * `options: OptionRow[]`; the payload builder maps them into the BE's SelectOption
 * shape (filtering rows with empty value or empty EN label).
 */
function OptionsEditor({
  hideValue = false,
  supportItemHelp = false,
  supportExclusion = false,
  supportReveal = false,
  supportOptionInternalName = false,
  supportOptionIcon = false,
  revealSteps,
  revealStepId,
  revealOwnerFieldId,
  templateId,
}: {
  hideValue?: boolean;
  /** Adds a (i) button per row that opens a modal to author per-item help
   *  (title + body, EN + FR, body supports markdown for links/images). Off by
   *  default — currently only BulletedList opts in. */
  supportItemHelp?: boolean;
  /** Adds a swap-arrows button per row that opens a Popover letting the
   *  author pick OTHER options to uncheck at runtime when this option is
   *  checked. CheckboxGroup-only — the other options-bearing types
   *  (RadioGroup / SingleSelect / BulletedList) don't accept it on the BE. */
  supportExclusion?: boolean;
  /** Adds an "Inline reveal" Select to the per-row Option behaviour modal,
   *  letting the author borrow another field on the same step for inline
   *  display when this option is selected. RadioGroup / CheckboxGroup — the
   *  two types that render a real option row to hang it beneath.
   *
   *  Either this or `supportExclusion` puts the Option behaviour button (and
   *  its grid column) on the row. */
  supportReveal?: boolean;
  /** Renders a compact second sub-row per option with an "AcroForm field"
   *  input. On for Radio / SingleSelect / CheckboxGroup — the BE refuses
   *  the mapping on other types. When set, the runtime pdf-lib fill
   *  CHECKS the named AcroForm boolean whenever this option is picked. */
  supportOptionInternalName?: boolean;
  /** Adds a card-icon picker per option, sourced from the OPTION_ICONS
   *  registry. RadioGroup / CheckboxGroup only — the two types whose options
   *  can render as cards. */
  supportOptionIcon?: boolean;
  /** Every step in the workflow (drives the eligible-reveal list). */
  revealSteps?: WorkflowStep[];
  /** Owning step id — only fields on this step are eligible to borrow, so
   *  authors can't accidentally cross-step a reveal. */
  revealStepId?: string;
  /** Owning CheckboxGroup field id — excluded from the eligible list
   *  (a field can't borrow itself). */
  revealOwnerFieldId?: string;
  /** Workflow template id — needed when supportItemHelp is on so the modal
   *  knows where to POST help-image uploads. */
  templateId?: string;
}) {
  const { t } = useTranslation();
  const [helpEditingIndex, setHelpEditingIndex] = useState<number | null>(null);
  const [behaviourEditingIndex, setBehaviourEditingIndex] = useState<number | null>(null);
  // 28px lang-prefix column + value column (when shown) + label + ONE 24px
  // action column. Every per-option button (help, Option behaviour, delete)
  // stacks vertically inside that single column, which spans the EN and FR
  // rows — so the column count no longer varies with which buttons are on.
  const gridCols = hideValue ? `28px 1fr 24px` : `28px 90px 1fr 24px`;
  return (
    <Form.Item label={t('workflow.builder.properties.options')} style={COMPACT_ITEM} required>
      <Form.List
        name="options"
        rules={[
          {
            validator: async (_, items) => {
              if (!items || items.length === 0) {
                return Promise.reject(new Error(t('workflow.builder.properties.optionsRequired')));
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {fields.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: 6,
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#6b7280',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  paddingLeft: 2,
                }}
              >
                <span />
                {!hideValue && <span>{t('workflow.builder.properties.optionValue')}</span>}
                <span>{t('workflow.builder.properties.optionLabel')}</span>
                <span />
              </div>
            )}
            {fields.map(({ key, name }) => (
              <OptionGroup
                key={key}
                name={name}
                hideValue={hideValue}
                supportItemHelp={supportItemHelp}
                supportExclusion={supportExclusion}
                supportReveal={supportReveal}
                supportOptionInternalName={supportOptionInternalName}
                supportOptionIcon={supportOptionIcon}
                onHelpEdit={() => setHelpEditingIndex(name)}
                onBehaviourEdit={() => setBehaviourEditingIndex(name)}
                onRemove={() => remove(name)}
                gridCols={gridCols}
              />
            ))}
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add({ value: '', labelEn: '', labelFr: '', valueFr: '' })}
              style={{ marginTop: 2 }}
            >
              {t('workflow.builder.properties.addOption')}
            </Button>
            <Form.ErrorList errors={errors} />
            {supportItemHelp && (
              <ItemHelpModal
                openIndex={helpEditingIndex}
                templateId={templateId}
                onClose={() => setHelpEditingIndex(null)}
              />
            )}
            {(supportExclusion || supportReveal) && (
              <OptionBehaviourModal
                openIndex={behaviourEditingIndex}
                supportExclusion={supportExclusion}
                supportReveal={supportReveal}
                revealSteps={revealSteps}
                revealStepId={revealStepId}
                revealOwnerFieldId={revealOwnerFieldId}
                onClose={() => setBehaviourEditingIndex(null)}
              />
            )}
          </div>
        )}
      </Form.List>
    </Form.Item>
  );
}

/**
 * Per-row "Option behaviour" trigger on the RadioGroup / CheckboxGroup
 * OptionsEditor. Opens {@link OptionBehaviourModal}; the toolbox icon paints
 * BMO blue when EITHER behaviour is already configured on this row, so an
 * author can see at a glance which options carry a rule without opening
 * anything.
 *
 * Deliberately a bare trigger with its own `useWatch` rather than a prop from
 * the parent: the row grid is 24px wide per action column, and the tint has to
 * stay live as the modal writes back.
 */
function OptionBehaviourButton({ rowIndex, onEdit }: { rowIndex: number; onEdit: () => void }) {
  const { t } = useTranslation();
  const row = Form.useWatch(['options', rowIndex]) as OptionRow | undefined;
  const isConfigured = (row?.clearsOnSelect?.length ?? 0) > 0 || Boolean(row?.revealFieldId);
  return (
    <Tooltip
      title={
        isConfigured
          ? t('workflow.builder.properties.optionBehaviourEditTooltip')
          : t('workflow.builder.properties.optionBehaviourAddTooltip')
      }
    >
      <Button
        type="text"
        size="small"
        style={OPTION_ACTION_BTN}
        icon={
          <ToolOutlined style={{ fontSize: 14, color: isConfigured ? '#0179C1' : '#6b7280' }} />
        }
        onClick={onEdit}
        aria-label={t('workflow.builder.properties.optionBehaviourAddTooltip')}
      />
    </Tooltip>
  );
}

/**
 * "Option behaviour" editor for one option row. Hosts the two rules that both
 * describe what happens at runtime when the option is selected:
 *
 *   - **Inline reveal** — Select listing other fields on the same step that
 *     this option can borrow for inline display. Rewrites the row's
 *     `revealFieldId`. RadioGroup + CheckboxGroup (`supportReveal`).
 *   - **Clears other options when checked** — Checkbox list over the OTHER
 *     options on the same group, rewriting `clearsOnSelect`. CheckboxGroup
 *     only (`supportExclusion`) — a single-pick radio is mutually exclusive
 *     by construction and the BE refuses the property there.
 *
 * A page modal rather than the popover this used to be: the reveal picker
 * lists every field on the step by label and needs room to be readable, and
 * the builder's other comparable editors ("Show conditionally", item help)
 * are all modals.
 *
 * Draft semantics match ConditionsEditorModal — edits are local until Save,
 * which writes through the AntD form and nudges autosave. That means this must
 * render INSIDE the form's AutoSaveTriggerContext provider (it does, via
 * OptionsEditor); outside it `useTriggerAutoSave` is a no-op and the edit is
 * silently lost.
 */
function OptionBehaviourModal({
  openIndex,
  supportReveal,
  supportExclusion,
  revealSteps,
  revealStepId,
  revealOwnerFieldId,
  onClose,
}: {
  openIndex: number | null;
  supportReveal?: boolean;
  supportExclusion?: boolean;
  revealSteps?: WorkflowStep[];
  revealStepId?: string;
  revealOwnerFieldId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const triggerAutoSave = useTriggerAutoSave();
  const open = openIndex !== null;
  // Watching the whole options array so the "other options" list stays live as
  // the author adds/edits/removes rows underneath. Reading via useWatch (not
  // getFieldValue) so we re-render on any change.
  const allOptions = (Form.useWatch('options') as OptionRow[] | undefined) ?? [];
  const row = openIndex === null ? undefined : allOptions[openIndex];

  const [draftReveal, setDraftReveal] = useState<string | null>(null);
  const [draftClears, setDraftClears] = useState<string[]>([]);
  // Re-seed on OPEN transitions only — deps are deliberately just `open` so a
  // keystroke in the options list underneath can't stomp the draft mid-edit.
  // Same rationale as ConditionsEditorModal.
  useEffect(() => {
    if (!open) return;
    const current = openIndex === null ? undefined : form.getFieldValue(['options', openIndex]);
    setDraftReveal((current as OptionRow | undefined)?.revealFieldId ?? null);
    setDraftClears((current as OptionRow | undefined)?.clearsOnSelect ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Other options on the same group — excludes the row itself by index AND by
  // value (so an unsaved row with no value doesn't fall back to matching every
  // empty-value row in a freshly-added pair).
  const ownValue = row?.value;
  const others = allOptions
    .map((o, i) => ({ option: o, index: i }))
    .filter(({ option, index }) => {
      if (index === openIndex) return false;
      // Drop rows that don't yet have a usable value — the author can't
      // declare a conflict against an option whose value will become
      // something else on save.
      const v = option.value?.trim();
      return Boolean(v) && v !== ownValue?.trim();
    });

  // Eligible reveal targets — every field on the same step, minus the owning
  // group itself and minus fields already borrowed by another option on this
  // same field. Cross-step references aren't offered (the BE doesn't refuse
  // them, we just don't expose them in the picker for safety).
  const eligibleRevealFields = (() => {
    if (!supportReveal || !revealSteps || !revealStepId) return [];
    const step = revealSteps.find((s) => s.id === revealStepId);
    if (!step) return [];
    // A field may be borrowed by at most ONE option across the whole step, so
    // the taken-list spans every group on it, not just this one. Two sources,
    // because they disagree about freshness:
    //   - THIS field's other options come from live form state (`allOptions`),
    //     so a borrow the author set seconds ago counts even though autosave
    //     may not have round-tripped yet.
    //   - Every OTHER field comes from the saved step snapshot.
    // The owning field is skipped in the step walk to avoid counting its own
    // stale saved options against its live ones.
    const borrowedByOthers = new Set(
      allOptions
        .map((o, i) => (i === openIndex ? null : (o.revealFieldId ?? null)))
        .filter((id): id is string => Boolean(id)),
    );
    for (const section of step.sections ?? []) {
      for (const r of section.rows ?? []) {
        for (const col of r.columns ?? []) {
          const f = col.field;
          if (!f || f.id === revealOwnerFieldId) continue;
          if (f.type !== 'RadioGroup' && f.type !== 'CheckboxGroup') continue;
          for (const o of f.options ?? []) {
            if (o.revealFieldId) borrowedByOthers.add(o.revealFieldId);
          }
        }
      }
    }
    const out: Array<{ id: string; label: string; takenByOther: boolean }> = [];
    for (const section of step.sections ?? []) {
      for (const r of section.rows ?? []) {
        for (const col of r.columns ?? []) {
          const f = col.field;
          if (!f) continue;
          if (f.id === revealOwnerFieldId) continue;
          out.push({
            id: f.id,
            label: f.labelEn || f.labelFr || f.type,
            takenByOther: borrowedByOthers.has(f.id),
          });
        }
      }
    }
    return out;
  })();

  const handleSave = () => {
    if (openIndex === null) return;
    if (supportReveal) {
      form.setFieldValue(['options', openIndex, 'revealFieldId'], draftReveal ?? null);
    }
    if (supportExclusion) {
      form.setFieldValue(['options', openIndex, 'clearsOnSelect'], draftClears);
    }
    triggerAutoSave();
    onClose();
  };

  return (
    <BmoModal
      open={open}
      title={t('workflow.builder.properties.optionBehaviourTitle')}
      onCancel={onClose}
      width={620}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {supportReveal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text strong style={{ fontSize: 13 }}>
              {t('workflow.builder.properties.revealTitle')}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
              {t('workflow.builder.properties.revealHelp')}
            </Text>
            {eligibleRevealFields.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('workflow.builder.properties.revealEmpty')}
              </Text>
            ) : (
              <Select
                allowClear
                style={{ width: '100%' }}
                value={draftReveal ?? undefined}
                onChange={(v) => setDraftReveal(v ?? null)}
                placeholder={t('workflow.builder.properties.revealPlaceholder')}
                options={eligibleRevealFields.map((f) => ({
                  value: f.id,
                  label: f.takenByOther
                    ? t('workflow.builder.properties.revealTakenSuffix', { label: f.label })
                    : f.label,
                  disabled: f.takenByOther,
                }))}
              />
            )}
          </div>
        )}
        {supportExclusion && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text strong style={{ fontSize: 13 }}>
              {t('workflow.builder.properties.exclusionTitle')}
            </Text>
            {others.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('workflow.builder.properties.exclusionEmpty')}
              </Text>
            ) : (
              <>
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  {t('workflow.builder.properties.exclusionHelp')}
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {others.map(({ option }) => (
                    <Checkbox
                      key={option.value}
                      checked={draftClears.includes(option.value)}
                      onChange={(e) =>
                        setDraftClears((prev) =>
                          e.target.checked
                            ? Array.from(new Set([...prev, option.value]))
                            : prev.filter((v) => v !== option.value),
                        )
                      }
                    >
                      {option.labelEn?.trim() || option.value}
                    </Checkbox>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </BmoModal>
  );
}

/**
 * Minimal originator editor for a RepeatableRow field's column template.
 * Each row is one column in the template: label (EN), type picker (limited
 * to the BE allow-list — ShortText / LongText / Number / SingleSelect /
 * RadioGroup / Date), and a required toggle. Add / remove columns inline.
 * Per-column options + maxLength + validation are deferred to a phase-2
 * authoring pass; for v1 the seed script pre-authors the richer columns
 * and the panel only needs to keep editing usable.
 */
/**
 * Multi-select that lets the originator pick one or more of the field's options
 * as "blocking answers" — when the advisor's value matches, FormShell disables
 * Next on the step. Reads the current options list from the form's `options`
 * field (Form.useWatch) so adding / removing an option in the OptionsEditor
 * immediately updates the picker. Empty selection = no blocking gate.
 *
 * For CheckboxGroup the advisor selects multiple values, so an AND/OR operator
 * decides whether the gate trips on ANY or ALL of the blocking values.
 * RadioGroup / SingleSelect pick a single value, so they're always OR and the
 * operator control is hidden.
 */
function BlockingValueSelect({ fieldType }: { fieldType: WorkflowFieldType }) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const watchedOptions = Form.useWatch('options') as OptionRow[] | undefined;
  const blockingValues = Form.useWatch('blockingValues') as string[] | undefined;
  const blockingValueOperator =
    (Form.useWatch('blockingValueOperator') as 'and' | 'or' | undefined) ?? 'or';
  const options = useMemo(() => watchedOptions ?? [], [watchedOptions]);
  const isCheckbox = fieldType === 'CheckboxGroup';
  const hasBlocking = (blockingValues?.length ?? 0) > 0;

  const triggerAutoSave = useTriggerAutoSave();
  // Auto-prune values whose option was removed (or whose value field was
  // edited). Without this, the form silently holds stale values the BE refuses
  // on save with no inline hint. When nothing survives, the alert switch +
  // message + operator decorations are wiped alongside — the BE refuses them
  // without a blocking value.
  useEffect(() => {
    if (!blockingValues || blockingValues.length === 0) return;
    const valid = blockingValues.filter((v) => options.some((o) => o.value === v));
    if (valid.length === blockingValues.length) return;
    form.setFieldValue('blockingValues', valid.length > 0 ? valid : undefined);
    if (valid.length === 0) {
      form.setFieldValue('blockingValueOperator', undefined);
      form.setFieldValue('blockContinueAlert', undefined);
      form.setFieldValue('blockContinueMessageEn', undefined);
      form.setFieldValue('blockContinueMessageFr', undefined);
    }
    triggerAutoSave();
  }, [blockingValues, options, form, triggerAutoSave]);

  const selectOptions = options
    .filter((o) => o.value)
    .map((o) => ({
      value: o.value,
      label: o.labelEn?.trim() ? `${o.labelEn} (${o.value})` : o.value,
    }));
  return (
    <>
      <Form.Item
        label={t('workflow.builder.properties.blockingValue')}
        name="blockingValues"
        style={COMPACT_ITEM}
        extra={t('workflow.builder.properties.blockingValueHint')}
      >
        <Select
          mode="multiple"
          options={selectOptions}
          placeholder={t('workflow.builder.properties.blockingValueNone')}
          allowClear
          onChange={(next: string[]) => {
            // Clearing the gate drops the decorations + operator too — the BE
            // refuses them without a blocking value.
            if (!next || next.length === 0) {
              form.setFieldValue('blockingValueOperator', undefined);
              form.setFieldValue('blockContinueAlert', undefined);
              form.setFieldValue('blockContinueMessageEn', undefined);
              form.setFieldValue('blockContinueMessageFr', undefined);
            }
          }}
        />
      </Form.Item>
      {hasBlocking && isCheckbox && (
        <Form.Item
          label={t('workflow.builder.properties.blockingValueOperator')}
          style={COMPACT_ITEM}
          extra={t('workflow.builder.properties.blockingValueOperatorHint')}
        >
          <BmoRadioGroup<'and' | 'or'>
            value={blockingValueOperator}
            onChange={(next) => form.setFieldValue('blockingValueOperator', next)}
            options={[
              { value: 'or', label: t('workflow.builder.properties.blockingValueOperatorOr') },
              { value: 'and', label: t('workflow.builder.properties.blockingValueOperatorAnd') },
            ]}
          />
        </Form.Item>
      )}
      {hasBlocking && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 12, color: '#111827' }}>
              {t('workflow.builder.properties.blockContinueAlert')}
            </div>
            <Form.Item name="blockContinueAlert" valuePropName="checked" noStyle>
              <Switch size="small" />
            </Form.Item>
          </div>
          <Form.Item
            label={t('workflow.builder.properties.blockContinueMessageEn')}
            name="blockContinueMessageEn"
            style={COMPACT_ITEM}
            extra={t('workflow.builder.properties.blockContinueMessageHint')}
          >
            <Input
              placeholder={t('workflow.builder.properties.blockContinueMessagePlaceholder')}
              maxLength={200}
            />
          </Form.Item>
          <Form.Item
            label={t('workflow.builder.properties.blockContinueMessageFr')}
            name="blockContinueMessageFr"
            style={COMPACT_ITEM}
          >
            <Input
              placeholder={t('workflow.builder.properties.blockContinueMessagePlaceholder')}
              maxLength={200}
            />
          </Form.Item>
        </>
      )}
    </>
  );
}

/** Per-column input-restriction values offered in the editor's Select. The
 *  empty string maps to "no restriction" — the value the BE stores as null. */
/**
 * Column types the domain accepts inside a repeatable template
 * (`IsAllowedRepeatableColumnType`). Until now the builder hardcoded every new
 * column to ShortText, so an originator could not create a Number column at
 * all — which in turn made the dual-unit `$/%` switch and any cross-row sum
 * rule unreachable from the UI, even though both were fully supported.
 */
const REPEATABLE_COLUMN_TYPES: { value: WorkflowFieldType; labelKey: string }[] = [
  { value: 'ShortText', labelKey: 'workflow.builder.properties.columnTypeShortText' },
  { value: 'LongText', labelKey: 'workflow.builder.properties.columnTypeLongText' },
  { value: 'Number', labelKey: 'workflow.builder.properties.columnTypeNumber' },
  { value: 'Date', labelKey: 'workflow.builder.properties.columnTypeDate' },
  { value: 'SingleSelect', labelKey: 'workflow.builder.properties.columnTypeSingleSelect' },
  { value: 'RadioGroup', labelKey: 'workflow.builder.properties.columnTypeRadioGroup' },
];

const REPEATABLE_INPUT_RESTRICTIONS = [
  { value: '', labelKey: 'workflow.builder.properties.inputRestrictionAny' },
  { value: 'digits', labelKey: 'workflow.builder.properties.inputRestrictionDigits' },
  { value: 'letters', labelKey: 'workflow.builder.properties.inputRestrictionLetters' },
  { value: 'alphanumeric', labelKey: 'workflow.builder.properties.inputRestrictionAlphanumeric' },
];

/**
 * One row in the repeatable-template editor: a compact line showing the column
 * name + a help (i) icon (when authored), an edit button that opens the
 * per-column settings modal, and a confirm-guarded delete button. The modal's
 * Form.Items bind live to this column's Form.List entry — the panel's own Save
 * persists them, consistent with the rest of this live-bound editor.
 */
function RepeatableColumnRow({
  field,
  onRemove,
}: {
  field: { key: number; name: number };
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Absolute paths from the form root — Form.useWatch ignores the Form.List
  // name prefix, so the full `repeatableTemplate` path is required.
  const labelEn = Form.useWatch(['repeatableTemplate', field.name, 'labelEn']) as
    string | undefined;
  const helpEn = Form.useWatch(['repeatableTemplate', field.name, 'helpEn']) as string | undefined;
  const helpFr = Form.useWatch(['repeatableTemplate', field.name, 'helpFr']) as string | undefined;
  const help = helpEn?.trim() || helpFr?.trim() || '';
  const displayName = labelEn?.trim() || t('workflow.builder.properties.columnUntitled');
  // Dual-unit is Number-only — "$" vs "%" is a unit of amount, so there is
  // nothing to switch on a text or date column. The BE refuses it elsewhere;
  // hiding the block keeps the originator from authoring a doomed save.
  const colType = Form.useWatch(['repeatableTemplate', field.name, 'type']) as
    WorkflowFieldType | undefined;
  const unitEnabled = Form.useWatch(['repeatableTemplate', field.name, 'unitToggleEnabled']) as
    boolean | undefined;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 500,
            color: labelEn?.trim() ? '#111827' : '#6b7280',
          }}
        >
          {displayName}
        </span>
        {help && (
          <Tooltip title={help}>
            <InfoCircleOutlined style={{ color: '#0179C1', fontSize: 12 }} />
          </Tooltip>
        )}
      </div>
      <Tooltip title={t('workflow.builder.properties.editColumn')}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => setOpen(true)}
          aria-label={t('workflow.builder.properties.editColumn')}
        />
      </Tooltip>
      <ConfirmPopover
        title={t('workflow.builder.properties.removeColumnConfirm')}
        onConfirm={onRemove}
      >
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          aria-label={t('workflow.builder.properties.removeColumn')}
        />
      </ConfirmPopover>
      <BmoModal
        open={open}
        onCancel={() => setOpen(false)}
        title={t('workflow.builder.properties.columnSettingsTitle')}
        footer={[
          <Button key="done" type="primary" onClick={() => setOpen(false)}>
            {t('common.done')}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Type first: it decides which of the blocks below apply. */}
          <Form.Item
            {...field}
            name={[field.name, 'type']}
            label={t('workflow.builder.properties.columnType')}
            style={{ marginBottom: 0 }}
          >
            <Select
              options={REPEATABLE_COLUMN_TYPES.map((c) => ({
                value: c.value,
                label: t(c.labelKey),
              }))}
            />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item
              {...field}
              name={[field.name, 'labelEn']}
              label={t('workflow.builder.properties.columnLabelEn')}
              rules={[{ required: true, whitespace: true }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder={t('workflow.builder.properties.columnLabelPlaceholder')} />
            </Form.Item>
            <Form.Item
              {...field}
              name={[field.name, 'labelFr']}
              label={t('workflow.builder.properties.columnLabelFr')}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder={t('workflow.builder.properties.columnLabelFrPlaceholder')} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item
              {...field}
              name={[field.name, 'inputRestriction']}
              label={t('workflow.builder.properties.inputRestriction')}
              style={{ marginBottom: 0 }}
            >
              <Select
                options={REPEATABLE_INPUT_RESTRICTIONS.map((c) => ({
                  value: c.value,
                  label: t(c.labelKey),
                }))}
                placeholder={t('workflow.builder.properties.inputRestriction')}
              />
            </Form.Item>
            <Form.Item
              {...field}
              name={[field.name, 'widthPercent']}
              label={t('workflow.builder.properties.columnWidth')}
              extra={t('workflow.builder.properties.columnWidthHint')}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                max={100}
                controls={false}
                addonAfter="%"
                placeholder={t('workflow.builder.properties.columnWidthPlaceholder')}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
          <Form.Item
            {...field}
            name={[field.name, 'required']}
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Checkbox>{t('workflow.builder.properties.required')}</Checkbox>
          </Form.Item>
          {/* Dual-unit money column. Number-only: "$" vs "%" is a unit of
              amount, so there is nothing to switch on a text or date column
              (the BE refuses it too). One switch, then what each unit must
              total — the two live together because the totals are part of what
              the unit MEANS. Either may be left blank: RIF constrains
              percentages only. */}
          {colType === 'Number' && (
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: 10,
                background: '#fafafa',
              }}
            >
              <Form.Item
                {...field}
                name={[field.name, 'unitToggleEnabled']}
                valuePropName="checked"
                style={{ marginBottom: unitEnabled ? 10 : 0 }}
                extra={t('workflow.builder.properties.unitToggleHint')}
              >
                <Checkbox>{t('workflow.builder.properties.unitToggle')}</Checkbox>
              </Form.Item>
              {unitEnabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Form.Item
                    {...field}
                    name={[field.name, 'unitToggle', 'defaultMode']}
                    label={t('workflow.builder.properties.unitToggleDefaultMode')}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="$"
                      options={[
                        {
                          value: 'dollar',
                          label: t('workflow.builder.properties.unitToggleDollar'),
                        },
                        {
                          value: 'percent',
                          label: t('workflow.builder.properties.unitTogglePercent'),
                        },
                      ]}
                    />
                  </Form.Item>
                  <span />
                  <Form.Item
                    {...field}
                    name={[field.name, 'unitToggle', 'dollarTargetConstant']}
                    label={t('workflow.builder.properties.unitToggleDollarTotal')}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      controls={false}
                      style={{ width: '100%' }}
                      placeholder={t('workflow.builder.properties.unitToggleNoTotal')}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'unitToggle', 'percentTargetConstant']}
                    label={t('workflow.builder.properties.unitTogglePercentTotal')}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      controls={false}
                      style={{ width: '100%' }}
                      placeholder={t('workflow.builder.properties.unitToggleNoTotal')}
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item
              {...field}
              name={[field.name, 'helpEn']}
              label={t('workflow.builder.properties.columnHelpEn')}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
                placeholder={t('workflow.builder.properties.columnHelpPlaceholder')}
              />
            </Form.Item>
            <Form.Item
              {...field}
              name={[field.name, 'helpFr']}
              label={t('workflow.builder.properties.columnHelpFr')}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
                placeholder={t('workflow.builder.properties.columnHelpPlaceholder')}
              />
            </Form.Item>
          </div>
          {/* Read-only: repeatable-column AcroForm handles are also assigned in
            the PDF Field Mapping tool. Kept bound so the value survives this
            panel's full-replace save. */}
          <Form.Item
            {...field}
            name={[field.name, 'internalName']}
            label={t('workflow.builder.properties.columnInternalName')}
            extra={t('workflow.builder.properties.columnInternalNameHintReadonly')}
            style={{ marginBottom: 0 }}
          >
            <Input
              disabled
              maxLength={200}
              placeholder={t('workflow.builder.properties.columnInternalNamePlaceholder')}
            />
          </Form.Item>
        </div>
      </BmoModal>
    </div>
  );
}

function RepeatableTemplateEditor({
  allSteps,
  stepId,
  fieldId,
}: {
  allSteps: WorkflowStep[];
  stepId: string | undefined;
  fieldId: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Form.Item
        label={t('workflow.builder.properties.maxRows')}
        name="maxRows"
        style={COMPACT_ITEM}
        extra={t('workflow.builder.properties.maxRowsHint')}
      >
        <InputNumber min={1} max={100} style={{ width: 120 }} controls={false} />
      </Form.Item>
      {/* Used to be implied by card layout, which made "table chrome with a
          locked first row" unauthorable. Now explicit. */}
      <Form.Item
        name="anchorFirstRow"
        valuePropName="checked"
        style={COMPACT_ITEM}
        extra={t('workflow.builder.properties.anchorFirstRowHint')}
      >
        <Checkbox>{t('workflow.builder.properties.anchorFirstRow')}</Checkbox>
      </Form.Item>
      <Form.Item label={t('workflow.builder.properties.repeatableTemplate')} style={COMPACT_ITEM}>
        <Form.List name="repeatableTemplate">
          {(fields, { add, remove }) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {fields.map((f) => (
                <RepeatableColumnRow key={f.key} field={f} onRemove={() => remove(f.name)} />
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() =>
                  // Every column ships as ShortText today — the originator
                  // never picks the type; the runtime always renders a text
                  // input with the column's inputRestriction applied.
                  add({
                    id: crypto.randomUUID(),
                    type: 'ShortText',
                    labelEn: '',
                    required: true,
                    inputRestriction: '',
                    internalName: '',
                    // widthPercent left unset — new columns default to an equal share.
                    helpEn: '',
                    helpFr: '',
                  })
                }
              >
                {t('workflow.builder.properties.addColumn')}
              </Button>
            </div>
          )}
        </Form.List>
      </Form.Item>
      <SumValidationEditor allSteps={allSteps} stepId={stepId} fieldId={fieldId} />
    </>
  );
}

/**
 * Authoring UI for a RepeatableRow field's cross-row sum rule. Renders three
 * form controls under the row template editor:
 *
 *   - **Column to sum** — Select of the current template's Number columns
 *   - **Target type** — Radio: Constant / Field
 *   - **Target value** — either a Number input (constant) or a Select of
 *     Number-typed fields on the same step (excluding the RepeatableRow
 *     itself, which would create a circular reference)
 *
 * The whole panel is optional — the outer buildProperties collapses to
 * null when the config is incomplete, so authors can safely leave the
 * section blank when their form doesn't need the check.
 */
function SumValidationEditor({
  allSteps,
  stepId,
  fieldId,
}: {
  allSteps: WorkflowStep[];
  stepId: string | undefined;
  fieldId: string;
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const template =
    (Form.useWatch('repeatableTemplate') as RepeatableTemplateRow[] | undefined) ?? [];
  const enabled = Form.useWatch('sumValidationEnabled') as boolean | undefined;
  const columnKey = Form.useWatch(['sumValidation', 'columnKey']) as string | undefined;
  const mode = Form.useWatch(['sumValidation', 'mode']) as 'constant' | 'field' | undefined;
  const columnOptions = template
    .filter((c) => c.type === 'Number' && c.id && (c.labelEn?.trim() || c.labelFr?.trim()))
    .map((c) => ({ value: c.id, label: c.labelEn?.trim() || c.labelFr?.trim() || c.id }));
  // Fields on the same step, minus the RepeatableRow being edited (no self-
  // reference) and minus non-Number types. Cross-step targets are not
  // exposed here — the domain accepts them, but the picker keeps the
  // originator's mental model simple.
  const currentStep = allSteps.find((s) => s.id === stepId);
  const fieldOptions: Array<{ value: string; label: string }> = [];
  if (currentStep && currentStep.sections) {
    for (const section of currentStep.sections) {
      for (const row of section.rows ?? []) {
        for (const col of row.columns ?? []) {
          const f = col.field;
          if (!f) continue;
          if (f.id === fieldId) continue;
          if (f.type !== 'Number') continue;
          fieldOptions.push({
            value: f.id,
            label: f.labelEn || f.labelFr || f.type,
          });
        }
      }
    }
  }
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
          {t('workflow.builder.properties.sumValidationTitle')}
        </div>
        <Form.Item name="sumValidationEnabled" valuePropName="checked" noStyle>
          <Switch
            size="small"
            onChange={(next) => {
              // Turning the rule off wipes the config so buildSumValidation
              // collapses to null on save; the fields are hidden anyway.
              if (!next) {
                form.setFieldValue(['sumValidation', 'columnKey'], undefined);
                form.setFieldValue(['sumValidation', 'mode'], undefined);
                form.setFieldValue(['sumValidation', 'targetConstant'], undefined);
                form.setFieldValue(['sumValidation', 'targetFieldId'], undefined);
              }
            }}
          />
        </Form.Item>
      </div>
      {enabled && (
        <>
          <Form.Item
            label={t('workflow.builder.properties.sumValidationColumn')}
            name={['sumValidation', 'columnKey']}
            style={COMPACT_ITEM}
          >
            <Select
              allowClear
              placeholder={t('workflow.builder.properties.sumValidationColumnPlaceholder')}
              options={columnOptions}
              onChange={(next) => {
                if (!next) {
                  // Clearing the column key clears the rest of the config so
                  // the payload builder collapses to null on save.
                  form.setFieldValue(['sumValidation', 'mode'], undefined);
                  form.setFieldValue(['sumValidation', 'targetConstant'], undefined);
                  form.setFieldValue(['sumValidation', 'targetFieldId'], undefined);
                }
              }}
            />
          </Form.Item>
          {columnKey && (
            <>
              <Form.Item
                label={t('workflow.builder.properties.sumValidationTargetType')}
                name={['sumValidation', 'mode']}
                style={COMPACT_ITEM}
              >
                <BmoRadioGroup<'constant' | 'field'>
                  value={mode}
                  onChange={(next) => form.setFieldValue(['sumValidation', 'mode'], next)}
                  options={[
                    {
                      value: 'constant',
                      label: t('workflow.builder.properties.sumValidationTargetConstant'),
                    },
                    {
                      value: 'field',
                      label: t('workflow.builder.properties.sumValidationTargetField'),
                    },
                  ]}
                />
              </Form.Item>
              {mode === 'constant' && (
                <Form.Item
                  label={t('workflow.builder.properties.sumValidationConstantLabel')}
                  name={['sumValidation', 'targetConstant']}
                  style={COMPACT_ITEM}
                >
                  <InputNumber
                    controls={false}
                    style={{ width: 200 }}
                    placeholder={t('workflow.builder.properties.sumValidationConstantPlaceholder')}
                  />
                </Form.Item>
              )}
              {mode === 'field' && (
                <Form.Item
                  label={t('workflow.builder.properties.sumValidationFieldLabel')}
                  name={['sumValidation', 'targetFieldId']}
                  style={COMPACT_ITEM}
                >
                  <Select
                    allowClear
                    placeholder={t('workflow.builder.properties.sumValidationFieldPlaceholder')}
                    options={fieldOptions}
                    notFoundContent={t('workflow.builder.properties.sumValidationNoFields')}
                  />
                </Form.Item>
              )}
              <Form.Item
                label={t('workflow.builder.properties.sumValidationLabelEn')}
                name={['sumValidation', 'labelEn']}
                style={COMPACT_ITEM}
              >
                <Input
                  allowClear
                  placeholder={t('workflow.builder.properties.sumValidationLabelPlaceholder')}
                />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.properties.sumValidationLabelFr')}
                name={['sumValidation', 'labelFr']}
                style={COMPACT_ITEM}
              >
                <Input
                  allowClear
                  placeholder={t('workflow.builder.properties.sumValidationLabelPlaceholder')}
                />
              </Form.Item>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Two-Select cascade for the cross-step prefill (§2). The first Select
 * lists PRIOR steps (workflow order) — the domain doesn't enforce prior-
 * step scope, but authoring one is nonsense (you can't seed from a step
 * the advisor hasn't reached yet). The second Select lists that step's
 * input fields, minus this one (self-reference is refused server-side)
 * and minus layout blocks + RepeatableRow (they have no scalar value to
 * copy). Clearing the step also clears the field so half-configured
 * refs never reach the wire.
 */
function SourceFieldPicker({
  allSteps,
  stepId,
  fieldId,
}: {
  allSteps: WorkflowStep[];
  stepId: string | undefined;
  fieldId: string;
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const stepPick = Form.useWatch('sourceFieldStepId') as string | undefined;
  // Prior steps only. `stepId === undefined` (unattached / draft field
  // being placed) shows every step — no upstream anchor to filter on.
  const currentIndex = stepId ? allSteps.findIndex((s) => s.id === stepId) : allSteps.length;
  const priorSteps = currentIndex >= 0 ? allSteps.slice(0, currentIndex) : allSteps;
  const stepOptions = priorSteps
    .filter((s) => s.type === 'DynamicForm')
    .map((s) => ({ value: s.id, label: s.titleEn || s.titleFr || s.type }));
  const chosenStep = allSteps.find((s) => s.id === stepPick);
  const fieldOptions: Array<{ value: string; label: string }> = [];
  if (chosenStep && chosenStep.sections) {
    for (const section of chosenStep.sections) {
      for (const row of section.rows ?? []) {
        for (const col of row.columns ?? []) {
          const f = col.field;
          if (!f) continue;
          if (f.id === fieldId) continue;
          if (isLayoutBlockType(f.type)) continue;
          if (f.type === 'RepeatableRow') continue;
          fieldOptions.push({ value: f.id, label: f.labelEn || f.labelFr || f.type });
        }
      }
    }
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
        {t('workflow.builder.properties.sourceFieldTitle')}
      </div>
      <div style={{ fontSize: 11, color: '#6b7280' }}>
        {t('workflow.builder.properties.sourceFieldHint')}
      </div>
      <Form.Item
        label={t('workflow.builder.properties.sourceFieldStep')}
        name="sourceFieldStepId"
        style={COMPACT_ITEM}
      >
        <Select
          allowClear
          placeholder={t('workflow.builder.properties.sourceFieldStepPlaceholder')}
          options={stepOptions}
          onChange={(next) => {
            if (!next) form.setFieldValue('sourceFieldFieldId', undefined);
          }}
          notFoundContent={t('workflow.builder.properties.sourceFieldNoPriorSteps')}
        />
      </Form.Item>
      {stepPick && (
        <Form.Item
          label={t('workflow.builder.properties.sourceFieldField')}
          name="sourceFieldFieldId"
          style={COMPACT_ITEM}
        >
          <Select
            allowClear
            placeholder={t('workflow.builder.properties.sourceFieldFieldPlaceholder')}
            options={fieldOptions}
            notFoundContent={t('workflow.builder.properties.sourceFieldNoFields')}
          />
        </Form.Item>
      )}
    </div>
  );
}

export type HelpDraft = {
  titleEn: string;
  titleFr: string;
  bodyEn: string;
  bodyFr: string;
};

export const EMPTY_HELP_DRAFT: HelpDraft = { titleEn: '', titleFr: '', bodyEn: '', bodyFr: '' };

/**
 * Shared help-bubble authoring modal. Used in two places:
 *  - Per-item help on BulletedList rows (each option carries its own bag).
 *  - Field-level help on input fields (the "?" next to the label at runtime).
 *
 * Pure-controlled: the parent owns the draft persistence (writes back to its
 * Form via setFieldValue/setFieldsValue inside onSave). The modal only mirrors
 * the incoming `initialDraft` into local state on open so the user can cancel
 * without committing typing.
 *
 * Body is a textarea since the runtime renders it as markdown (links + images).
 * Save validates EN title + EN body are paired (both set, or neither) — mirrors
 * the BE pair rule so the originator sees the error in the modal instead of as
 * a 400 after closing it.
 *
 * Chrome reuses `BmoModal` (same blue-header shell as Create / Edit Workflow).
 */
/**
 * Trailing-image regex. Matches a single markdown `![alt](url)` at the very end
 * of a body string (optionally preceded by whitespace). The runtime renders the
 * full markdown body as-is, but the authoring modal pulls a trailing image out
 * into a structured "Attached image" row so the originator doesn't see raw
 * markdown in the textarea.
 *
 * Only the LAST image is structured — inline images mid-paragraph stay in the
 * text. In practice originators attach one image per help bubble.
 */
const TRAILING_IMAGE_RE = /\s*!\[([^\]]*)\]\(([^)]+)\)\s*$/;

/** Split a body string into prose + optional attached image. */
function splitBody(body: string): { prose: string; image: { alt: string; url: string } | null } {
  const m = body.match(TRAILING_IMAGE_RE);
  if (!m) return { prose: body, image: null };
  return {
    prose: body.slice(0, m.index).replace(/\s+$/, ''),
    image: { alt: m[1] ?? '', url: m[2] ?? '' },
  };
}

/** Recombine prose + optional image into a single body string (BE wire shape). */
function joinBody(prose: string, image: { alt: string; url: string } | null): string {
  const trimmed = prose.trimEnd();
  if (!image) return trimmed;
  const sep = trimmed ? '\n\n' : '';
  return `${trimmed}${sep}![${image.alt}](${image.url})`;
}

function HelpAuthorModal({
  open,
  title,
  initialDraft,
  templateId,
  onCancel,
  onSave,
}: {
  open: boolean;
  title: string;
  initialDraft: HelpDraft;
  /** Workflow template id — used to construct the image-upload endpoint URL.
   *  Omitted when the modal is used outside a template context (no upload UI). */
  templateId: string | undefined;
  onCancel: () => void;
  onSave: (draft: HelpDraft) => void;
}) {
  const { t } = useTranslation();
  // Local UI state is split across prose + image per locale so the textarea
  // never contains raw image markdown. We recombine into a single body string
  // at save time (see joinBody) so the BE schema stays flat.
  type LocalState = {
    titleEn: string;
    titleFr: string;
    proseEn: string;
    proseFr: string;
    imageEn: { alt: string; url: string } | null;
    imageFr: { alt: string; url: string } | null;
  };
  const [state, setState] = useState<LocalState>(() => seed(initialDraft));
  const [error, setError] = useState<string | null>(null);
  const [uploadingLocale, setUploadingLocale] = useState<'en' | 'fr' | null>(null);

  // Re-seed on each open. We can't `key` the modal (BmoModal uses
  // forceRender) and the parent always passes a fresh `initialDraft` per
  // open — useEffect tied to `open` is the cleanest way to reset.
  useEffect(() => {
    if (open) {
      setState(seed(initialDraft));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function seed(d: HelpDraft): LocalState {
    const en = splitBody(d.bodyEn);
    const fr = splitBody(d.bodyFr);
    return {
      titleEn: d.titleEn,
      titleFr: d.titleFr,
      proseEn: en.prose,
      proseFr: fr.prose,
      imageEn: en.image,
      imageFr: fr.image,
    };
  }

  const handleSave = () => {
    const titleEn = state.titleEn.trim();
    const titleFr = state.titleFr.trim();
    const bodyEn = joinBody(state.proseEn, state.imageEn);
    const bodyFr = joinBody(state.proseFr, state.imageFr);
    if ((titleEn && !bodyEn) || (!titleEn && bodyEn)) {
      setError(t('workflow.builder.properties.itemHelpPairedRequired'));
      return;
    }
    onSave({ titleEn, titleFr, bodyEn, bodyFr });
  };

  /**
   * Image-upload handler — same for either locale. Streams the picked file to
   * the BE help-images endpoint and assigns it to the locale's structured
   * image slot. The textarea never gets touched.
   */
  const triggerImageUpload = (locale: 'en' | 'fr') => async () => {
    if (!templateId || uploadingLocale) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingLocale(locale);
      try {
        const result = await workflowsApi.uploadHelpImage(templateId, file);
        // Alt text defaults to the original filename (sans extension) — gives
        // screen readers and broken-image fallbacks something meaningful.
        const alt = file.name.replace(/\.[^.]+$/, '');
        const image = { alt, url: result.url };
        setState((s) => (locale === 'en' ? { ...s, imageEn: image } : { ...s, imageFr: image }));
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e);
        void message.error(
          t('workflow.builder.properties.helpImageUploadFailed', { error: errMsg }),
        );
      } finally {
        setUploadingLocale(null);
      }
    };
    input.click();
  };

  const removeImage = (locale: 'en' | 'fr') =>
    setState((s) => (locale === 'en' ? { ...s, imageEn: null } : { ...s, imageFr: null }));

  // Delete is only offered when the modal opened on existing help — saving the
  // empty draft clears all four scalars in the parent form (same path as Save).
  const hasExistingHelp = Boolean(
    initialDraft.titleEn || initialDraft.titleFr || initialDraft.bodyEn || initialDraft.bodyFr,
  );

  return (
    <BmoModal
      open={open}
      title={title}
      onCancel={onCancel}
      width={620}
      footer={[
        ...(hasExistingHelp
          ? [
              <Button
                key="delete"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onSave({ ...EMPTY_HELP_DRAFT })}
                style={{ float: 'left' }}
              >
                {t('workflow.builder.properties.helpDelete')}
              </Button>,
            ]
          : []),
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form layout="vertical" component="div">
        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            label={t('workflow.builder.properties.helpTitleEn')}
            style={{ flex: 1, marginBottom: 16 }}
          >
            <Input
              value={state.titleEn}
              onChange={(e) => setState((s) => ({ ...s, titleEn: e.target.value }))}
              placeholder={t('workflow.builder.properties.helpTitlePlaceholder') ?? ''}
            />
          </Form.Item>
          <Form.Item
            label={t('workflow.builder.properties.helpTitleFr')}
            style={{ flex: 1, marginBottom: 16 }}
          >
            <Input
              value={state.titleFr}
              onChange={(e) => setState((s) => ({ ...s, titleFr: e.target.value }))}
            />
          </Form.Item>
        </div>

        {/* EN body + image */}
        <Form.Item label={t('workflow.builder.properties.helpBodyEn')} style={{ marginBottom: 6 }}>
          <Input.TextArea
            rows={4}
            value={state.proseEn}
            onChange={(e) => setState((s) => ({ ...s, proseEn: e.target.value }))}
            placeholder={t('workflow.builder.properties.helpBodyPlaceholder') ?? ''}
          />
        </Form.Item>
        <LinkTip />
        <ImageAttachmentRow
          image={state.imageEn}
          locale="en"
          disabled={!templateId}
          uploading={uploadingLocale === 'en'}
          onUpload={triggerImageUpload('en')}
          onRemove={() => removeImage('en')}
        />

        {/* FR body + image */}
        <Form.Item
          label={t('workflow.builder.properties.helpBodyFr')}
          style={{ marginTop: 16, marginBottom: 6 }}
        >
          <Input.TextArea
            rows={4}
            value={state.proseFr}
            onChange={(e) => setState((s) => ({ ...s, proseFr: e.target.value }))}
          />
        </Form.Item>
        <ImageAttachmentRow
          image={state.imageFr}
          locale="fr"
          disabled={!templateId}
          uploading={uploadingLocale === 'fr'}
          onUpload={triggerImageUpload('fr')}
          onRemove={() => removeImage('fr')}
        />

        {error && (
          <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
            {error}
          </Text>
        )}
      </Form>
    </BmoModal>
  );
}

/**
 * Single-line link-syntax tip shown once under the EN body. Originators apply
 * the same syntax in both locales, so we don't duplicate the tip under FR.
 *
 * The wording is deliberately "To add a link, type:" rather than "Add a link:".
 * The latter read as a BUTTON — originators expected clicking it to insert a
 * link and reported the panel as broken when nothing happened. The example
 * likewise names its two slots ("Link text" / example.com) instead of using a
 * real site, so the shape of the syntax is what stands out rather than Google.
 */
function LinkTip() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: '#eff6ff',
        border: '1px solid #c7e0f4',
        borderRadius: 6,
        fontSize: 12,
        color: '#1f2937',
        marginBottom: 8,
      }}
    >
      <InfoCircleOutlined style={{ color: '#0179C1', fontSize: 14 }} />
      <strong>{t('workflow.builder.properties.helpBodyLinkTipTitle')}</strong>
      <code
        style={{
          padding: '2px 6px',
          background: '#fff',
          border: '1px solid #dbe7f5',
          borderRadius: 4,
          fontSize: 11,
          color: '#0179C1',
        }}
      >
        {t('workflow.builder.properties.helpBodyLinkTipExample')}
      </code>
    </div>
  );
}

/**
 * Compact row under a body textarea showing whichever of two states the help
 * body's attached image is in: "no image, with an Upload button" or "image
 * attached, with its filename and a Remove button". Same shape per locale.
 */
function ImageAttachmentRow({
  image,
  locale,
  disabled,
  uploading,
  onUpload,
  onRemove,
}: {
  image: { alt: string; url: string } | null;
  locale: 'en' | 'fr';
  disabled: boolean;
  uploading: boolean;
  onUpload: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  if (disabled) return null;
  const labelKey =
    locale === 'en'
      ? 'workflow.builder.properties.helpImageEnLabel'
      : 'workflow.builder.properties.helpImageFrLabel';

  if (!image) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: '#6b7280',
          marginBottom: 4,
        }}
      >
        <span style={{ flex: 1 }}>{t(labelKey)}</span>
        <Button
          type="default"
          size="small"
          icon={<PictureOutlined />}
          loading={uploading}
          onClick={onUpload}
        >
          {t('workflow.builder.properties.helpImageUpload')}
        </Button>
      </div>
    );
  }

  // Filename = the imageId.ext we stored as the URL's last segment.
  const filename = image.url.split('/').pop() ?? image.url;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: '#f3f6fb',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        fontSize: 12,
        marginBottom: 4,
      }}
    >
      <PictureOutlined style={{ color: '#0179C1', fontSize: 14 }} />
      <span style={{ flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {filename}
      </span>
      <Button
        type="link"
        size="small"
        icon={<PictureOutlined />}
        loading={uploading}
        onClick={onUpload}
        style={{ padding: 0, fontSize: 12 }}
      >
        {t('workflow.builder.properties.helpImageReplace')}
      </Button>
      <Button
        type="link"
        size="small"
        danger
        icon={<DeleteOutlined />}
        onClick={onRemove}
        style={{ padding: 0, fontSize: 12 }}
      >
        {t('workflow.builder.properties.helpImageRemove')}
      </Button>
    </div>
  );
}

/**
 * Per-item (BulletedList option) wrapper around `HelpAuthorModal`. Reads the
 * row's current help bag from the parent Form via getFieldValue, hands it to
 * the modal as the initial draft, and on save writes the four scalars back
 * to the same Form.List item. Empty strings collapse to null so the wire
 * stays tidy (BE pre-clears half-filled bags anyway).
 */
function ItemHelpModal({
  openIndex,
  templateId,
  onClose,
}: {
  openIndex: number | null;
  templateId: string | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const triggerAutoSave = useTriggerAutoSave();
  const isOpen = openIndex !== null;
  const initialDraft: HelpDraft =
    openIndex === null
      ? EMPTY_HELP_DRAFT
      : (() => {
          const row = (form.getFieldValue(['options', openIndex]) as OptionRow | undefined) ?? {
            value: '',
            labelEn: '',
          };
          return {
            titleEn: row.helpTitleEn ?? '',
            titleFr: row.helpTitleFr ?? '',
            bodyEn: row.helpBodyEn ?? '',
            bodyFr: row.helpBodyFr ?? '',
          };
        })();
  return (
    <HelpAuthorModal
      open={isOpen}
      title={t('workflow.builder.properties.itemHelpModalTitle')}
      initialDraft={initialDraft}
      templateId={templateId}
      onCancel={onClose}
      onSave={(d) => {
        if (openIndex === null) return;
        form.setFieldValue(['options', openIndex, 'helpTitleEn'], d.titleEn || null);
        form.setFieldValue(['options', openIndex, 'helpTitleFr'], d.titleFr || null);
        form.setFieldValue(['options', openIndex, 'helpBodyEn'], d.bodyEn || null);
        form.setFieldValue(['options', openIndex, 'helpBodyFr'], d.bodyFr || null);
        triggerAutoSave();
        onClose();
      }}
    />
  );
}

/**
 * Field-level wrapper. Same modal, different parent shape — the field-level
 * help lives at the top level of the FieldEditor's form (`helpTitleEn`,
 * `helpTitleFr`, `helpBodyEn`, `helpBodyFr`), not nested under `options[i]`.
 */
function FieldHelpModal({
  open,
  templateId,
  onClose,
}: {
  open: boolean;
  templateId: string | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance<FormValues>();
  const triggerAutoSave = useTriggerAutoSave();
  const v = form.getFieldsValue(['helpTitleEn', 'helpTitleFr', 'helpBodyEn', 'helpBodyFr']);
  const initialDraft: HelpDraft = {
    titleEn: v.helpTitleEn ?? '',
    titleFr: v.helpTitleFr ?? '',
    bodyEn: v.helpBodyEn ?? '',
    bodyFr: v.helpBodyFr ?? '',
  };
  return (
    <HelpAuthorModal
      open={open}
      title={t('workflow.builder.properties.fieldHelpModalTitle')}
      initialDraft={initialDraft}
      templateId={templateId}
      onCancel={onClose}
      onSave={(d) => {
        form.setFieldsValue({
          helpTitleEn: d.titleEn || undefined,
          helpTitleFr: d.titleFr || undefined,
          helpBodyEn: d.bodyEn || undefined,
          helpBodyFr: d.bodyFr || undefined,
        });
        triggerAutoSave();
        onClose();
      }}
    />
  );
}

/**
 * Right rail of the builder. Renders an edit form for the currently-selected field;
 * the controls switch per field type so the FE only ever sends keys the BE accepts
 * for that type. SingleSelect + RadioGroup share the structured OptionsEditor above.
 *
 * Save fires the parent's PATCH; no autosave — the user clicks Save explicitly so
 * the API-call count is bounded. Delete fires the parent's DELETE.
 */
export function FieldPropertiesPanel({
  field,
  templateId,
  stepId,
  location,
  allSteps,
  onSave,
  onRemove,
  onConditionsChanged,
  onClose,
  onSaveFieldConditions,
  hideSaveAsCustom,
}: Props) {
  // The panel is only mounted when a field is selected — caller-driven dismissal
  // via onClose so the canvas reclaims the freed width when the user hits X.
  if (!field) return null;

  // Key on field.id so switching to a different column gets a fresh form
  // instance (no stale-values race when AntD reuses the form between mounts).
  return (
    <FieldEditor
      key={field.id}
      field={field}
      templateId={templateId}
      stepId={stepId}
      location={location}
      allSteps={allSteps}
      onSave={onSave}
      onRemove={onRemove}
      onConditionsChanged={onConditionsChanged}
      onClose={onClose}
      onSaveFieldConditions={onSaveFieldConditions}
      hideSaveAsCustom={hideSaveAsCustom}
    />
  );
}

function FieldEditor({
  field,
  templateId,
  stepId,
  location,
  allSteps,
  onSave,
  onRemove,
  onConditionsChanged,
  onClose,
  onSaveFieldConditions,
  hideSaveAsCustom,
}: {
  field: WorkflowField;
  templateId: string | undefined;
  stepId: string | undefined;
  location: Props['location'];
  allSteps: WorkflowStep[];
  onSave: Props['onSave'];
  onRemove: Props['onRemove'];
  onConditionsChanged: Props['onConditionsChanged'];
  onClose: Props['onClose'];
  onSaveFieldConditions: Props['onSaveFieldConditions'];
  hideSaveAsCustom: Props['hideSaveAsCustom'];
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  // Reactive watch on the required toggle so the EN/FR required-error inputs
  // (rendered directly below the Required checkbox) appear/disappear in
  // lockstep with it — the underlying form value for those two inputs is
  // preserved across hide/show so flipping the toggle off doesn't blow the
  // originator's authored copy away.
  const requiredChecked = Form.useWatch<boolean | undefined>('required', form);
  // Watch the semantic `validate` rule at panel scope so the Text and
  // Validation sections (which now live in separate collapsible groups) can
  // both react to it: a masked rule (sin / phone / postal) fully owns the
  // format, so min/max length (Text) and input restriction (Validation) hide
  // to avoid the originator authoring a conflicting override. ShortText-only
  // on the wire; undefined elsewhere leaves both controls visible.
  // The two option-layout switches are mutually exclusive: a card grid can't
  // also be a vertical control-and-label list. Each one disables the other
  // rather than silently overriding it, so the originator can see why the
  // second switch won't move. Card icons only mean something in card layout,
  // so the per-option icon pickers hide entirely when it's off.
  const verticalOptionsOn = Form.useWatch<boolean | undefined>('verticalOptions', form) === true;
  const optionCardLayoutOn = Form.useWatch<boolean | undefined>('optionCardLayout', form) === true;
  // Icons belong to the tile grid; a full-width row card doesn't draw one.
  const showOptionIcons = optionCardLayoutOn && !verticalOptionsOn;
  const validateVal = Form.useWatch<string | undefined>('validate', form);
  const ruleOwnsFormat =
    validateVal === 'sin' || validateVal === 'phone' || validateVal === 'postal';
  // Whether the collapsible Validation section has any control to show for this
  // field type — drives whether the section header renders at all (empty
  // sections never appear). The required-message override no longer lives in
  // this section (it sits directly under the Required checkbox), so it does
  // not factor into this guard.
  const showValidationSection =
    field.type === 'ShortText' ||
    field.type === 'LongText' ||
    field.type === 'SingleSelect' ||
    field.type === 'RadioGroup' ||
    field.type === 'Date' ||
    field.type === 'Number';
  // Compact ↔ expanded panel width. Seeded from localStorage so the
  // originator's preference sticks across field selections + page reloads.
  // Wrapped in a try/catch so SSR / private-browsing failures don't crash.
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(PANEL_EXPANDED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [fieldHelpModalOpen, setFieldHelpModalOpen] = useState(false);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [savingConditions, setSavingConditions] = useState(false);
  const [saveAsCustomOpen, setSaveAsCustomOpen] = useState(false);
  const [savingAsCustom, setSavingAsCustom] = useState(false);
  // Guards the destructive Delete action behind a confirm modal — deleting a
  // placed field removes it and all its settings from the form, which the
  // undo-less builder can't walk back.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // True while a picked file is uploading via workflowsApi.uploadHelpImage.
  // Disables the Upload/Replace buttons so a double-click can't fire two
  // PUTs into S3.
  const [uploadingImage, setUploadingImage] = useState(false);
  const hasConditions = (field.conditions?.length ?? 0) > 0;
  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(PANEL_EXPANDED_KEY, next ? '1' : '0');
      } catch {
        /* ignore quota / private-mode */
      }
      return next;
    });
  };

  /**
   * Image upload handler — streams the picked file through the BE help-images
   * endpoint (general-purpose image storage scoped to a workflow) and assigns
   * the returned URL onto the form's `imageUrl` field. The save itself happens
   * later when the user clicks the panel's Save button.
   */
  const triggerImageFieldUpload = async () => {
    if (!templateId || uploadingImage) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingImage(true);
      try {
        const result = await workflowsApi.uploadHelpImage(templateId, file);
        form.setFieldValue('imageUrl', result.url);
        // Seed Alt text with the filename (sans extension) if the originator
        // hasn't already authored one — a meaningful default for screen
        // readers + broken-image fallbacks.
        const altEn = form.getFieldValue('altEn');
        if (!altEn) form.setFieldValue('altEn', file.name.replace(/\.[^.]+$/, ''));
        handleValuesChange();
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e);
        void message.error(t('workflow.builder.properties.imageUploadFailed', { error: errMsg }));
      } finally {
        setUploadingImage(false);
      }
    };
    input.click();
  };

  const removeImageField = () => {
    form.setFieldValue('imageUrl', '');
    handleValuesChange();
  };

  // Initial values are captured at mount (FieldEditor is keyed on field.id so
  // switching fields remounts with the new initial state). Critically we do
  // NOT keep the form synced to `field` afterwards — the parent's 20s lock-
  // poll re-fetches the workflow and produces a new `field` reference every
  // tick, which would otherwise wipe whatever the user is typing.
  const initialValues = useMemo<FormValues>(
    () => ({
      labelEn: field.labelEn,
      // Seed an empty FR label with the same "fr-" placeholder convention the
      // shell uses at placement time — only reachable for fields placed before
      // that default existed. Keeps the required-FR rule from blocking saves.
      labelFr: field.labelFr || `FR-${field.labelEn}`,
      required: field.required,
      placeholder: field.placeholder ?? undefined,
      minLength: field.minLength ?? undefined,
      maxLength: field.maxLength ?? undefined,
      min: field.min ?? undefined,
      max: field.max ?? undefined,
      step: field.step ?? undefined,
      integerOnly: field.integerOnly ?? undefined,
      options: field.options.map((o) => ({
        value: o.value,
        valueFr: o.valueFr ?? o.value,
        labelEn: o.labelEn,
        labelFr: o.labelFr ?? '',
        helpTitleEn: o.helpTitleEn ?? null,
        helpTitleFr: o.helpTitleFr ?? null,
        helpBodyEn: o.helpBodyEn ?? null,
        helpBodyFr: o.helpBodyFr ?? null,
        // Carry the CheckboxGroup mutual-exclusion list into the form so the
        // OptionsEditor's per-row Conflicts popover can render it. Defaulted
        // to [] (not null) so the form value stays type-stable while editing.
        clearsOnSelect: o.clearsOnSelect ?? [],
        // Inline-reveal target — the OptionsEditor's per-row picker writes
        // back into this slot.
        revealFieldId: o.revealFieldId ?? null,
        // Card icon key — the per-row picker writes back into this slot.
        icon: o.icon ?? null,
        // Per-option AcroForm checkbox mappings are managed in the PDF Field
        // Mapping tool (not this panel) and preserved by the BE across edits, so
        // they're intentionally not carried into the form here.
      })),
      helpTitleEn: field.helpTitleEn ?? undefined,
      helpTitleFr: field.helpTitleFr ?? undefined,
      helpBodyEn: field.helpBodyEn ?? undefined,
      helpBodyFr: field.helpBodyFr ?? undefined,
      hintEn: field.hintEn ?? undefined,
      hintFr: field.hintFr ?? undefined,
      requiredMessageEn: field.requiredMessageEn ?? undefined,
      requiredMessageFr: field.requiredMessageFr ?? undefined,
      fontSize: field.fontSize ?? undefined,
      // Sanitize a stored alpha hex (#rrggbbaa from rows saved before the
      // 2026-06-24 ColorPicker fix) down to opaque before it lands in the
      // form. Without this AntD's ColorPicker prints a disabledAlpha
      // warning on every open and the user-facing colour can drift.
      color: toOpaqueHex(field.color) ?? undefined,
      // Sliders default to 20 px (matches the runtime renderer's fallback for
      // unset values) so the originator sees the actual baseline on open
      // rather than the slider snapping to 0.
      marginTop: field.marginTop ?? DEFAULT_TEXT_MARGIN_PX,
      marginBottom: field.marginBottom ?? DEFAULT_TEXT_MARGIN_PX,
      // Default to bulleted (disc) when the originator hasn't picked one.
      listStyle: field.listStyle ?? 'disc',
      fontWeight: field.fontWeight ?? 'regular',
      // 'any' is the form-side sentinel for "no restriction" — buildProperties
      // collapses it back to null on the wire. Keeps the radio group from
      // opening with nothing checked.
      inputRestriction: field.inputRestriction ?? 'any',
      // Notice defaults: light info-tint bg + info icon — same shape the
      // palette card emits via defaultProperties(), so the panel never opens
      // with a different default than what the originator placed. For
      // non-Notice fields these never reach the wire (buildProperties scopes
      // them per type).
      backgroundColor:
        toOpaqueHex(field.backgroundColor) ?? (field.type === 'Notice' ? '#EFF6FF' : undefined),
      icon: field.icon ?? (field.type === 'Notice' ? 'info' : undefined),
      validate: field.validate ?? undefined,
      regexPattern: field.regexPattern ?? undefined,
      regexMessageEn: field.regexMessageEn ?? undefined,
      regexMessageFr: field.regexMessageFr ?? undefined,
      addressAutocomplete: field.addressAutocomplete ?? undefined,
      addressRole: field.addressRole ?? undefined,
      minAge: field.minAge ?? undefined,
      numberFormat: field.numberFormat ?? undefined,
      blockingValues: field.blockingValues ?? undefined,
      blockingValueOperator: field.blockingValueOperator ?? 'or',
      blockContinueAlert: field.blockContinueAlert ?? false,
      blockContinueMessageEn: field.blockContinueMessageEn ?? undefined,
      blockContinueMessageFr: field.blockContinueMessageFr ?? undefined,
      verticalOptions: field.verticalOptions ?? false,
      // `displayAs` is shared with Notice, so only read it as a card flag on
      // the option-group types that use the 'list' | 'card' vocabulary.
      optionCardLayout:
        (field.type === 'RadioGroup' || field.type === 'CheckboxGroup') &&
        field.displayAs === 'card',
      repeatableTemplate: field.repeatableTemplate?.map((c) => ({
        id: c.id,
        type: c.type,
        labelEn: c.labelEn,
        labelFr: c.labelFr || undefined,
        required: c.required,
        maxLength: c.maxLength ?? undefined,
        // Carry every per-column property the BE supports so an
        // edit-save round trip preserves seed/API-authored fields the
        // editor UI doesn't surface yet.
        placeholder: c.placeholder ?? undefined,
        // Dual-unit block: the master switch is derived from whether a toggle
        // was authored at all, mirroring sumValidationEnabled.
        unitToggleEnabled: !!c.unitToggle,
        unitToggle: c.unitToggle
          ? {
              defaultMode: c.unitToggle.defaultMode ?? 'dollar',
              dollarTargetConstant: c.unitToggle.dollarTargetConstant ?? undefined,
              percentTargetConstant: c.unitToggle.percentTargetConstant ?? undefined,
            }
          : undefined,
        options: c.options.length
          ? c.options.map((o) => ({
              value: o.value,
              labelEn: o.labelEn,
              labelFr: o.labelFr ?? undefined,
              helpTitleEn: o.helpTitleEn ?? undefined,
              helpTitleFr: o.helpTitleFr ?? undefined,
              helpBodyEn: o.helpBodyEn ?? undefined,
              helpBodyFr: o.helpBodyFr ?? undefined,
            }))
          : undefined,
        validate: c.validate ?? undefined,
        inputRestriction: c.inputRestriction ?? '',
        internalName: c.internalName ?? '',
        widthPercent: c.widthPercent ?? undefined,
        helpEn: c.helpEn ?? undefined,
        helpFr: c.helpFr ?? undefined,
      })),
      maxRows: field.maxRows ?? undefined,
      anchorFirstRow: field.anchorFirstRow ?? undefined,
      // Image defaults — kept here so the panel opens with the same shape the
      // palette card emits via defaultProperties(). For non-Image types these
      // never reach the wire (buildProperties scopes them per type).
      imageUrl: field.imageUrl ?? undefined,
      imageWidthSize: field.imageWidthSize ?? (field.type === 'Image' ? 'full' : undefined),
      imageAlignment: field.imageAlignment ?? (field.type === 'Image' ? 'left' : undefined),
      captionEn: field.captionEn ?? undefined,
      captionFr: field.captionFr ?? undefined,
      altEn: field.altEn ?? undefined,
      altFr: field.altFr ?? undefined,
      readOnly: field.readOnly ?? undefined,
      sourceValue: field.sourceValue ?? undefined,
      // Cross-row sum rule — hydrate from the domain shape. Mode is
      // inferred from which target slot is set so the form's radio
      // (constant vs field) lands on the correct branch on open. The
      // master toggle starts on only when a rule is already persisted.
      sumValidationEnabled: !!field.sumValidation,
      sumValidation: field.sumValidation
        ? {
            columnKey: field.sumValidation.columnKey ?? undefined,
            mode:
              field.sumValidation.targetFieldId != null
                ? 'field'
                : field.sumValidation.targetConstant != null
                  ? 'constant'
                  : undefined,
            targetConstant: field.sumValidation.targetConstant ?? undefined,
            targetFieldId: field.sumValidation.targetFieldId ?? undefined,
            labelEn: field.sumValidation.labelEn ?? undefined,
            labelFr: field.sumValidation.labelFr ?? undefined,
          }
        : undefined,
      uniquenessGroup: field.uniquenessGroup ?? undefined,
      minSelections: field.minSelections ?? undefined,
      sourceFieldStepId: field.sourceField?.stepId ?? undefined,
      sourceFieldFieldId: field.sourceField?.fieldId ?? undefined,
    }),
    // Re-derive only when the identity changes — which only happens on
    // remount (because of `key={field.id}`), so this useMemo runs exactly
    // once per editor mount. The exhaustive-deps lint would have us list
    // every primitive on `field`, but that would defeat the purpose:
    // we *want* later identity-only changes to be ignored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field.id],
  );

  const meta = metaForFieldType(field.type);

  // ---- Auto-save (replaces the old bottom Save button) -------------------
  // Three-state save indicator next to the panel title:
  //   'saved'  — no pending edits, last PATCH succeeded
  //   'saving' — debounce window elapsed and a PATCH is in flight
  //   'error'  — last PATCH 4xx/5xx'd; tooltip surfaces the message
  type AutoSaveStatus = 'saved' | 'saving' | 'error';
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('saved');
  const [autoSaveErrorText, setAutoSaveErrorText] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Counts in-flight PATCHes so a stale response from an earlier change
  // can't overwrite a newer one's status (race when the user types fast).
  const inFlightCountRef = useRef(0);

  // Single shared persistence routine — called by both the debounced
  // auto-save path and the flush-on-close path. Builds the payload from
  // the current form state, runs validation (so an invalid label/colour
  // etc. surfaces as an error rather than silently corrupting state),
  // and reflects the result into the indicator. Wrapped in useCallback
  // so the cleanup-on-unmount effect has a stable reference.
  const persistNow = useCallback(async () => {
    inFlightCountRef.current += 1;
    setAutoSaveStatus('saving');
    try {
      const values = await form.validateFields();
      const properties = buildProperties(field.type, values);
      await onSave({
        labelEn: values.labelEn,
        labelFr: values.labelFr ?? null,
        required: values.required,
        properties,
      });
      inFlightCountRef.current -= 1;
      // Only flip back to 'saved' when no other request is still pending —
      // prevents an early-success from masking a later-fail.
      if (inFlightCountRef.current === 0) {
        setAutoSaveStatus('saved');
        setAutoSaveErrorText(null);
      }
    } catch (err) {
      inFlightCountRef.current -= 1;
      const msg = err instanceof Error ? err.message : String(err);
      setAutoSaveErrorText(msg);
      setAutoSaveStatus('error');
    }
  }, [field.type, form, onSave]);

  // AntD Form fires onValuesChange synchronously on every controlled-input
  // change. Debounce 500ms so rapid typing produces one PATCH, not N.
  // 500ms is short enough to feel ambient (Notion/Linear/Google Docs sit
  // around here) and long enough that the BE validator only sees complete
  // values, not half-typed strings.
  const handleValuesChange = () => {
    setAutoSaveStatus('saving');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void persistNow();
    }, 500);
  };

  // If the originator closes the panel mid-debounce, flush the pending
  // save synchronously so they don't lose the last edits. Also clears any
  // outstanding timer on unmount so a stale callback can't fire after
  // the panel is gone.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        // Flush — fire-and-forget; nothing else to await on unmount.
        void persistNow();
      }
    };
  }, [persistNow]);

  // Save the live form state (not the possibly-stale `field` prop) as a reusable
  // custom element. A standalone field has no visibility conditions on the wire
  // to begin with (PlaceWorkflowFieldRequest carries none), so there's nothing
  // to strip here — stripping only matters once this field is nested inside a
  // saved Section/Step.
  const handleSaveAsCustom = async ({
    nameEn,
    nameFr,
  }: {
    nameEn: string;
    nameFr: string | null;
  }) => {
    setSavingAsCustom(true);
    try {
      const values = await form.validateFields();
      const properties = buildProperties(field.type, values);
      await customElementsApi.saveField(nameEn, nameFr, {
        type: field.type,
        labelEn: values.labelEn,
        labelFr: values.labelFr ?? null,
        required: values.required,
        properties,
      });
      void message.success(t('workflow.customElements.savedField'));
      setSaveAsCustomOpen(false);
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.saveFailed'));
    } finally {
      setSavingAsCustom(false);
    }
  };

  return (
    <aside
      style={{
        ...RAIL_BASE,
        width: expanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH_COMPACT,
      }}
    >
      {/* Sticky header row: icon + title + expand toggle + close X. Border-
          bottom acts as the separator so the form below has visual breathing
          room. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 14,
          marginBottom: 16,
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: meta.tintBg,
            color: meta.tintFg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          {meta.icon()}
        </span>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            // Tight gap + line-heights so the two-line text block sits the same
            // visual height as the 32px icon next to it.
            gap: 0,
          }}
        >
          <Text strong style={{ fontSize: 14, lineHeight: 1.2, display: 'block' }}>
            {t('workflow.builder.properties.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
            {t(fieldDisplayLabelKey(field))}
          </Text>
        </div>
        {/* Auto-save status dot — tooltip explains the colour. Hovering on
            the error state surfaces the BE message so the originator can
            tell why a save 4xx'd. */}
        <Tooltip
          title={
            autoSaveStatus === 'saved'
              ? t('workflow.builder.properties.autoSaveSaved')
              : autoSaveStatus === 'saving'
                ? t('workflow.builder.properties.autoSaveSaving')
                : t('workflow.builder.properties.autoSaveError', {
                    error: autoSaveErrorText ?? '',
                  })
          }
        >
          <span
            role="status"
            aria-label={
              autoSaveStatus === 'saved'
                ? t('workflow.builder.properties.autoSaveSaved')
                : autoSaveStatus === 'saving'
                  ? t('workflow.builder.properties.autoSaveSaving')
                  : t('workflow.builder.properties.autoSaveError', {
                      error: autoSaveErrorText ?? '',
                    })
            }
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 999,
              background:
                autoSaveStatus === 'saved'
                  ? '#16a34a' // green-600
                  : autoSaveStatus === 'saving'
                    ? '#f59e0b' // amber-500
                    : '#dc2626', // red-600
              transition: 'background-color 140ms ease',
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Tooltip
          title={t(
            expanded
              ? 'workflow.builder.properties.collapsePanel'
              : 'workflow.builder.properties.expandPanel',
          )}
        >
          <Button
            type="text"
            size="small"
            icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleExpanded}
            aria-label={t(
              expanded
                ? 'workflow.builder.properties.collapsePanel'
                : 'workflow.builder.properties.expandPanel',
            )}
          />
        </Tooltip>
        <Tooltip title={t('workflow.builder.properties.close')}>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            aria-label={t('workflow.builder.properties.close')}
          />
        </Tooltip>
      </div>

      {/* Tighten the space between each field label and its input. AntD vertical
          form items default to ~8px label bottom padding; this scoped override
          drops it to 2px without touching forms anywhere else in the app. */}
      <style>{`
        .builder-props-form .ant-form-item-label { padding-bottom: 2px; }
        .builder-props-form .ant-form-item-label > label { height: auto; }
      `}</style>
      <AutoSaveTriggerContext.Provider value={handleValuesChange}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={initialValues}
          onValuesChange={handleValuesChange}
          className="builder-props-form"
        >
          <Form.Item
            label={
              isLayoutBlockType(field.type)
                ? field.type === 'Divider'
                  ? t('workflow.builder.properties.dividerNameEn')
                  : t('workflow.builder.properties.contentEn')
                : t('workflow.builder.properties.labelEn')
            }
            name="labelEn"
            rules={[{ required: true, message: t('workflow.builder.properties.labelEnRequired') }]}
            style={COMPACT_ITEM}
          >
            {/* Paragraph + Notice carry longer multi-line copy — give them a
              textarea so Enter creates a line break (rendered as <br> via
              remark-breaks) without horizontal scroll. */}
            {field.type === 'Paragraph' || field.type === 'Notice' ? (
              <Input.TextArea rows={4} />
            ) : (
              <Input />
            )}
          </Form.Item>

          <Form.Item
            label={
              isLayoutBlockType(field.type)
                ? field.type === 'Divider'
                  ? t('workflow.builder.properties.dividerNameFr')
                  : t('workflow.builder.properties.contentFr')
                : t('workflow.builder.properties.labelFr')
            }
            name="labelFr"
            rules={[{ required: true, message: t('workflow.builder.properties.labelFrRequired') }]}
            style={COMPACT_ITEM}
          >
            {field.type === 'Paragraph' || field.type === 'Notice' ? (
              <Input.TextArea rows={4} />
            ) : (
              <Input />
            )}
          </Form.Item>

          {/* Rich-text affordance hint — shown for the three field types whose
            label is rendered as markdown at runtime. Same one-line callout
            as the help-modal hint, kept consistent so originators don't
            have to relearn the syntax. */}
          {(field.type === 'StaticText' ||
            field.type === 'Paragraph' ||
            field.type === 'Notice') && <LinkTip />}

          {/* Required-flag only applies to inputs. Hide on layout blocks — the BE
            ignores it for those types but surfacing it would be misleading.
            Kept out of a collapsible section — always visible below the label. */}
          {!isLayoutBlockType(field.type) && (
            <Form.Item name="required" valuePropName="checked" style={COMPACT_ITEM}>
              <Checkbox>{t('workflow.builder.properties.required')}</Checkbox>
            </Form.Item>
          )}

          {/* Originator-authored override for the required-error copy shown at
            runtime when this field is required AND the advisor leaves it
            empty. Only meaningful when Required is checked, so the inputs
            surface directly below the checkbox and hide otherwise. The form
            values stick around either way so toggling off + back on doesn't
            lose the typed copy. */}
          {!isLayoutBlockType(field.type) && requiredChecked && (
            <>
              <Form.Item
                label={t('workflow.builder.properties.requiredMessageEn')}
                name="requiredMessageEn"
                style={COMPACT_ITEM}
              >
                <Input
                  placeholder={t('workflow.builder.properties.requiredMessageEnPlaceholder')}
                  maxLength={200}
                />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.properties.requiredMessageFr')}
                name="requiredMessageFr"
                style={COMPACT_ITEM}
              >
                <Input
                  placeholder={t('workflow.builder.properties.requiredMessageFrPlaceholder')}
                  maxLength={200}
                />
              </Form.Item>
            </>
          )}

          {/* Read-only flag — disables the input at runtime + skips the
            required-empty check. Typically paired with SourceValue on the
            same field (advisor session prefill), but the two are
            independent. Layout blocks refuse both at the BE. */}
          {!isLayoutBlockType(field.type) && (
            <Form.Item name="readOnly" valuePropName="checked" style={COMPACT_ITEM}>
              <Checkbox>{t('workflow.builder.properties.readOnly')}</Checkbox>
            </Form.Item>
          )}

          {/* ── Layout-block styling ─────────────────────────────────────────
            Left ungrouped (not collapsible). These controls only ever render
            for layout blocks, which don't surface the input-field sections
            below, so their editor still reads as one flat, short list. */}

          {/* Static-text styling — font size + color. Only StaticText / Paragraph
            (the two layout blocks that render text content). Divider has no
            text to style. */}
          {(field.type === 'StaticText' || field.type === 'Paragraph') && (
            <>
              <Form.Item
                label={t('workflow.builder.properties.fontSize')}
                name="fontSize"
                style={COMPACT_ITEM}
              >
                <Select
                  allowClear
                  placeholder={t('workflow.builder.properties.fontSizeDefault')}
                  options={[
                    { value: 12, label: '12 px' },
                    { value: 14, label: '14 px' },
                    { value: 16, label: '16 px' },
                    { value: 18, label: '18 px' },
                    { value: 20, label: '20 px' },
                    { value: 24, label: '24 px' },
                    { value: 28, label: '28 px' },
                    { value: 32, label: '32 px' },
                  ]}
                />
              </Form.Item>
              {/* Color + Font weight share one row so they read as paired
                typography controls. ColorPicker shows the hex next to the
                swatch; the Weight Select keys to a CSS weight number on the
                runtime side. */}
              <div style={{ display: 'flex', gap: 8, ...COMPACT_ITEM }}>
                <Form.Item
                  label={t('workflow.builder.properties.color')}
                  name="color"
                  style={{ flex: 1, marginBottom: 0 }}
                  getValueFromEvent={pickerHex}
                >
                  <ColorPicker showText format="hex" disabledAlpha />
                </Form.Item>
                <Form.Item
                  label={t('workflow.builder.properties.fontWeight')}
                  name="fontWeight"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Select
                    options={[
                      { value: 'thin', label: t('workflow.builder.properties.weightThin') },
                      { value: 'regular', label: t('workflow.builder.properties.weightRegular') },
                      { value: 'bold', label: t('workflow.builder.properties.weightBold') },
                    ]}
                  />
                </Form.Item>
              </div>
              <Form.Item
                label={t('workflow.builder.properties.marginTop')}
                name="marginTop"
                style={COMPACT_ITEM}
                extra={t('workflow.builder.properties.marginDefault')}
              >
                <Slider
                  min={0}
                  max={30}
                  step={5}
                  marks={MARGIN_MARKS}
                  tooltip={{ formatter: (v) => `${v} px` }}
                />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.properties.marginBottom')}
                name="marginBottom"
                style={COMPACT_ITEM}
              >
                <Slider
                  min={0}
                  max={30}
                  step={5}
                  marks={MARGIN_MARKS}
                  tooltip={{ formatter: (v) => `${v} px` }}
                />
              </Form.Item>
            </>
          )}

          {/* Notice — tinted callout. BackgroundColor + Icon are Notice-only;
            Color / FontWeight / MarginTop / MarginBottom come from the
            shared text-styling surface, exposed here without FontSize. */}
          {field.type === 'Notice' && (
            <>
              <div style={{ display: 'flex', gap: 8, ...COMPACT_ITEM }}>
                <Form.Item
                  label={t('workflow.builder.properties.backgroundColor')}
                  name="backgroundColor"
                  style={{ flex: 1, marginBottom: 0 }}
                  getValueFromEvent={pickerHex}
                >
                  <ColorPicker showText format="hex" disabledAlpha />
                </Form.Item>
                <Form.Item
                  label={t('workflow.builder.properties.noticeIcon')}
                  name="icon"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Select
                    options={[
                      { value: 'info', label: t('workflow.builder.properties.noticeIconInfo') },
                      {
                        value: 'warning',
                        label: t('workflow.builder.properties.noticeIconWarning'),
                      },
                      {
                        value: 'success',
                        label: t('workflow.builder.properties.noticeIconSuccess'),
                      },
                      { value: 'tip', label: t('workflow.builder.properties.noticeIconTip') },
                    ]}
                  />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', gap: 8, ...COMPACT_ITEM }}>
                <Form.Item
                  label={t('workflow.builder.properties.color')}
                  name="color"
                  style={{ flex: 1, marginBottom: 0 }}
                  getValueFromEvent={pickerHex}
                >
                  <ColorPicker showText format="hex" disabledAlpha />
                </Form.Item>
                <Form.Item
                  label={t('workflow.builder.properties.fontWeight')}
                  name="fontWeight"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Select
                    options={[
                      { value: 'thin', label: t('workflow.builder.properties.weightThin') },
                      { value: 'regular', label: t('workflow.builder.properties.weightRegular') },
                      { value: 'bold', label: t('workflow.builder.properties.weightBold') },
                    ]}
                  />
                </Form.Item>
              </div>
              <Form.Item
                label={t('workflow.builder.properties.marginTop')}
                name="marginTop"
                style={COMPACT_ITEM}
                extra={t('workflow.builder.properties.marginDefault')}
              >
                <Slider
                  min={0}
                  max={30}
                  step={5}
                  marks={MARGIN_MARKS}
                  tooltip={{ formatter: (v) => `${v} px` }}
                />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.properties.marginBottom')}
                name="marginBottom"
                style={COMPACT_ITEM}
              >
                <Slider
                  min={0}
                  max={30}
                  step={5}
                  marks={MARGIN_MARKS}
                  tooltip={{ formatter: (v) => `${v} px` }}
                />
              </Form.Item>
            </>
          )}

          {/* Image — static image with optional bilingual caption + alt text.
            Upload streams through the BE help-images endpoint (general
            workflow-scoped image storage); the returned URL lives on the
            field. Width / Alignment selectors drive the runtime renderer. */}
          {field.type === 'Image' && (
            <ImageAuthoring
              templateId={templateId}
              uploading={uploadingImage}
              onUpload={triggerImageFieldUpload}
              onRemove={removeImageField}
            />
          )}

          {/* ── Options ──────────────────────────────────────────────────────
            SingleSelect / RadioGroup / CheckboxGroup option lists, plus
            BulletedList items + list style. */}
          {(field.type === 'SingleSelect' ||
            field.type === 'RadioGroup' ||
            field.type === 'CheckboxGroup' ||
            field.type === 'BulletedList') && (
            <CollapsibleSection
              id="options"
              title={t('workflow.builder.properties.sectionOptions')}
              defaultOpen
            >
              {/* Vertical layout switch — RadioGroup / CheckboxGroup only.
                Off (default) flows the options horizontally; on stacks them
                one per line in the preview + advisor runtime. SingleSelect is
                a dropdown and BulletedList a layout block, so neither gets a
                layout to flip. Switch first with its label inline to the
                right, above the option list. */}
              {(field.type === 'RadioGroup' || field.type === 'CheckboxGroup') && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 16,
                    marginBottom: 16,
                    marginLeft: 8,
                  }}
                >
                  <Form.Item name="verticalOptions" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <div style={{ fontSize: 12, color: '#111827' }}>
                    {t('workflow.builder.properties.verticalOptions')}
                  </div>
                </div>
              )}
              {/* Card option layout — renders each option as a bordered tile
                  with an optional icon instead of a control-and-label row.
                  Same two types as the vertical switch above; stored on the
                  shared `displayAs` property. Per-option icons are picked in
                  the option rows below and only render while this is on. */}
              {(field.type === 'RadioGroup' || field.type === 'CheckboxGroup') && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 16,
                    marginBottom: 16,
                    marginLeft: 8,
                  }}
                >
                  <Form.Item name="optionCardLayout" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <div style={{ fontSize: 12, color: '#111827' }}>
                    {t('workflow.builder.properties.optionCardLayout')}
                  </div>
                </div>
              )}
              {/* SingleSelect renders as a dropdown — no option list to lay
                  out as cards, so it gets no icon picker. RadioGroup does. */}
              {field.type === 'SingleSelect' && <OptionsEditor supportOptionInternalName />}
              {/* Reveals but no exclusion: a radio is mutually exclusive by
                  construction, so `clearsOnSelect` is meaningless here (and
                  the BE refuses it). The Option behaviour modal renders only
                  its Inline reveal section for this type. */}
              {field.type === 'RadioGroup' && (
                <OptionsEditor
                  supportReveal
                  supportOptionInternalName
                  supportOptionIcon={showOptionIcons}
                  revealSteps={allSteps}
                  revealStepId={stepId}
                  revealOwnerFieldId={field.id}
                />
              )}
              {field.type === 'CheckboxGroup' && (
                <>
                  <OptionsEditor
                    supportExclusion
                    supportReveal
                    supportOptionInternalName
                    supportOptionIcon={showOptionIcons}
                    revealSteps={allSteps}
                    revealStepId={stepId}
                    revealOwnerFieldId={field.id}
                  />
                  {/* Cardinality floor. `required` only ever means "one or
                      more"; set this to the option count for an
                      all-must-be-acknowledged gate. */}
                  <Form.Item
                    label={t('workflow.builder.properties.minSelectionsLabel')}
                    name="minSelections"
                    style={COMPACT_ITEM}
                    extra={t('workflow.builder.properties.minSelectionsHint')}
                  >
                    <InputNumber
                      min={1}
                      max={Math.max(1, field.options?.length ?? 1)}
                      style={{ width: '100%' }}
                      controls={false}
                      placeholder={t('workflow.builder.properties.minSelectionsPlaceholder')}
                    />
                  </Form.Item>
                </>
              )}
              {/* BulletedList — list items reuse the OptionsEditor; ListStyle
                Select flips bullets vs. numbers at runtime. */}
              {field.type === 'BulletedList' && (
                <>
                  <Form.Item
                    label={t('workflow.builder.properties.listStyle')}
                    name="listStyle"
                    style={COMPACT_ITEM}
                  >
                    <Select
                      options={[
                        { value: 'disc', label: t('workflow.builder.properties.listStyleDisc') },
                        {
                          value: 'decimal',
                          label: t('workflow.builder.properties.listStyleDecimal'),
                        },
                        { value: 'dash', label: t('workflow.builder.properties.listStyleDash') },
                        {
                          value: 'checkmark',
                          label: t('workflow.builder.properties.listStyleCheckmark'),
                        },
                      ]}
                    />
                  </Form.Item>
                  <OptionsEditor hideValue supportItemHelp templateId={templateId} />
                </>
              )}
            </CollapsibleSection>
          )}

          {/* ── Row options ──────────────────────────────────────────────────
            RepeatableRow max rows, column template + cross-row sum rule. */}
          {field.type === 'RepeatableRow' && (
            <CollapsibleSection
              id="rowOptions"
              title={t('workflow.builder.properties.sectionRowOptions')}
              defaultOpen
            >
              <RepeatableTemplateEditor allSteps={allSteps} stepId={stepId} fieldId={field.id} />
            </CollapsibleSection>
          )}

          {/* ── Text ─────────────────────────────────────────────────────────
            Placeholder, length, hint, and the help bubble. Shown for every
            input type (hint + help always apply); ShortText / LongText also
            get placeholder + length. */}
          {!isLayoutBlockType(field.type) && (
            <CollapsibleSection id="text" title={t('workflow.builder.properties.sectionText')}>
              {(field.type === 'ShortText' || field.type === 'LongText') && (
                <Form.Item
                  label={t('workflow.builder.properties.placeholder')}
                  name="placeholder"
                  style={COMPACT_ITEM}
                >
                  <Input />
                </Form.Item>
              )}
              {/* Min + Max length share one row so they read as a paired range
                control. LongText doesn't expose a minimum (it's a textarea — an
                arbitrary lower bound is rarely meaningful), so on that type Max
                alone takes the whole row. Hidden entirely when a masked validate
                rule (sin / phone / postal) already owns the format — see the
                Validation section. */}
              {(field.type === 'ShortText' || field.type === 'LongText') && !ruleOwnsFormat && (
                <div style={{ display: 'flex', gap: 8, ...COMPACT_ITEM }}>
                  {field.type === 'ShortText' && (
                    <Form.Item
                      label={t('workflow.builder.properties.minLength')}
                      name="minLength"
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  )}
                  <Form.Item
                    label={t('workflow.builder.properties.maxLength')}
                    name="maxLength"
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </div>
              )}
              {/* Hint — short note rendered under the input at runtime (e.g.
                "MM/DD/YYYY", "Exactly as it appears on the SIN card"). EN+FR
                independent — leave one blank to skip it. */}
              <Form.Item
                label={t('workflow.builder.properties.hintEn')}
                name="hintEn"
                style={COMPACT_ITEM}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={t('workflow.builder.properties.hintFr')}
                name="hintFr"
                style={COMPACT_ITEM}
              >
                <Input />
              </Form.Item>
              {/* Help bubble — spec §6.3. EN+FR title + body, paired (BE refuses
                unpaired). The four values live on the hidden Form.Items below so
                buildHelp reads from one source; the button opens the shared
                HelpAuthorModal. Keeping the slots mounted (hidden, not removed)
                means getFieldValue / setFieldsValue always have something to bind
                to even while this section is collapsed. */}
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const v = form.getFieldsValue([
                    'helpTitleEn',
                    'helpTitleFr',
                    'helpBodyEn',
                    'helpBodyFr',
                  ]);
                  const hasHelp = Boolean(v.helpTitleEn?.trim() && v.helpBodyEn?.trim());
                  return (
                    <Button
                      type="default"
                      icon={
                        <InfoCircleOutlined style={{ color: hasHelp ? '#0179C1' : '#6b7280' }} />
                      }
                      onClick={() => setFieldHelpModalOpen(true)}
                      block
                    >
                      {hasHelp
                        ? t('workflow.builder.properties.fieldHelpEdit')
                        : t('workflow.builder.properties.fieldHelpAdd')}
                    </Button>
                  );
                }}
              </Form.Item>
              <Form.Item name="helpTitleEn" hidden noStyle>
                <Input />
              </Form.Item>
              <Form.Item name="helpTitleFr" hidden noStyle>
                <Input />
              </Form.Item>
              <Form.Item name="helpBodyEn" hidden noStyle>
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="helpBodyFr" hidden noStyle>
                <Input.TextArea />
              </Form.Item>
              <FieldHelpModal
                open={fieldHelpModalOpen}
                templateId={templateId}
                onClose={() => setFieldHelpModalOpen(false)}
              />
            </CollapsibleSection>
          )}

          {/* ── Validation ───────────────────────────────────────────────────
            Semantic rule, cross-field uniqueness, input restriction, blocking
            answer, min age, and number bounds. (The required-error override
            now sits directly under the Required checkbox above.) */}
          {showValidationSection && (
            <CollapsibleSection
              id="validation"
              title={t('workflow.builder.properties.sectionValidation')}
            >
              {/* Validate (semantic rule). sin / email / phone / postal are
                ShortText-only; picking a masked one (sin / phone / postal) makes
                the length + restriction controls redundant (the rule fully owns
                the format), so those hide below. Email is variable-length so they
                stay. 'regex' is offered on ShortText AND LongText and reveals a
                pattern + bilingual message below; it does NOT own the format, so
                the length controls stay usable alongside it. */}
              {(field.type === 'ShortText' || field.type === 'LongText') && (
                <Form.Item
                  label={t('workflow.builder.properties.validate')}
                  name="validate"
                  style={COMPACT_ITEM}
                  tooltip={t('workflow.builder.properties.validateHint')}
                >
                  <Select
                    allowClear
                    placeholder={t('workflow.builder.properties.validateNone')}
                    options={[
                      // sin/email/phone/postal are single-line only — the BE
                      // refuses them on LongText.
                      ...(field.type === 'ShortText'
                        ? [
                            { value: 'sin', label: t('workflow.builder.properties.validateSin') },
                            {
                              value: 'email',
                              label: t('workflow.builder.properties.validateEmail'),
                            },
                            {
                              value: 'phone',
                              label: t('workflow.builder.properties.validatePhone'),
                            },
                            {
                              value: 'postal',
                              label: t('workflow.builder.properties.validatePostal'),
                            },
                          ]
                        : []),
                      { value: 'regex', label: t('workflow.builder.properties.validateRegex') },
                    ]}
                  />
                </Form.Item>
              )}
              {/* Regex pattern + bilingual error message — shown when the regex
                rule is picked. The pattern is required by the BE; the message is
                optional (renderer falls back to a generic error when blank). */}
              {(field.type === 'ShortText' || field.type === 'LongText') &&
                validateVal === 'regex' && (
                  <>
                    <Form.Item
                      label={t('workflow.builder.properties.regexPattern')}
                      name="regexPattern"
                      style={COMPACT_ITEM}
                      tooltip={t('workflow.builder.properties.regexPatternHint')}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: t('workflow.builder.properties.regexPatternRequired'),
                        },
                        {
                          validator: (_, value: string | undefined) => {
                            if (!value || !value.trim()) return Promise.resolve();
                            try {
                              RegExp(value);
                              return Promise.resolve();
                            } catch {
                              return Promise.reject(
                                new Error(t('workflow.builder.properties.regexPatternInvalid')),
                              );
                            }
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder={t('workflow.builder.properties.regexPatternPlaceholder')}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('workflow.builder.properties.regexMessageEn')}
                      name="regexMessageEn"
                      style={COMPACT_ITEM}
                    >
                      <Input
                        placeholder={t('workflow.builder.properties.regexMessagePlaceholder')}
                        maxLength={200}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('workflow.builder.properties.regexMessageFr')}
                      name="regexMessageFr"
                      style={COMPACT_ITEM}
                    >
                      <Input
                        placeholder={t('workflow.builder.properties.regexMessagePlaceholder')}
                        maxLength={200}
                      />
                    </Form.Item>
                  </>
                )}
              {field.type === 'ShortText' && ruleOwnsFormat && (
                <Form.Item style={COMPACT_ITEM}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {t('workflow.builder.properties.validateOwnsFormatHint')}
                  </Text>
                </Form.Item>
              )}
              {/* Cross-field uniqueness — ShortText only. Free-form tag; two
                fields carrying the same non-empty tag are checked pairwise at
                runtime and duplicates block Next + paint an inline error. */}
              {field.type === 'ShortText' && (
                <Form.Item
                  label={t('workflow.builder.properties.uniquenessGroupLabel')}
                  name="uniquenessGroup"
                  style={COMPACT_ITEM}
                  extra={t('workflow.builder.properties.uniquenessGroupHint')}
                >
                  <Input
                    maxLength={100}
                    placeholder={t('workflow.builder.properties.uniquenessGroupPlaceholder')}
                  />
                </Form.Item>
              )}
              {(field.type === 'ShortText' || field.type === 'LongText') && !ruleOwnsFormat && (
                <Form.Item
                  label={t('workflow.builder.properties.inputRestriction')}
                  name="inputRestriction"
                  style={COMPACT_ITEM}
                >
                  {/* Three short mutually-exclusive options sit on one row.
                    Uses BmoRadioGroup for consistency with the runtime
                    Title / Gender radios. The 'any' sentinel maps back
                    to null in buildProperties so the wire stays clean. */}
                  <BmoRadioGroup<string>
                    options={[
                      {
                        value: 'any',
                        label: t('workflow.builder.properties.inputRestrictionAny'),
                      },
                      {
                        value: 'digits',
                        label: t('workflow.builder.properties.inputRestrictionDigits'),
                      },
                      {
                        value: 'letters',
                        label: t('workflow.builder.properties.inputRestrictionLetters'),
                      },
                    ]}
                  />
                </Form.Item>
              )}
              {/* Blocking-value picker — RadioGroup / SingleSelect / CheckboxGroup.
                Lets the originator nominate option(s) that disable Next on the
                step when the advisor's answer matches (e.g. "Resident = No" →
                form short-circuits). CheckboxGroup adds an AND/OR operator. An
                empty selection clears the gate. */}
              {(field.type === 'SingleSelect' ||
                field.type === 'RadioGroup' ||
                field.type === 'CheckboxGroup') && <BlockingValueSelect fieldType={field.type} />}
              {field.type === 'Date' && (
                <Form.Item
                  label={t('workflow.builder.properties.minAge')}
                  name="minAge"
                  style={COMPACT_ITEM}
                  extra={t('workflow.builder.properties.minAgeHint')}
                >
                  <InputNumber min={0} max={150} style={{ width: '100%' }} />
                </Form.Item>
              )}
              {field.type === 'Number' && (
                <>
                  <Form.Item
                    label={t('workflow.builder.properties.min')}
                    name="min"
                    style={COMPACT_ITEM}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    label={t('workflow.builder.properties.max')}
                    name="max"
                    style={COMPACT_ITEM}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    label={t('workflow.builder.properties.numberFormat')}
                    name="numberFormat"
                    style={COMPACT_ITEM}
                  >
                    <Select
                      allowClear
                      placeholder={t('workflow.builder.properties.numberFormatNone')}
                      options={[
                        {
                          value: 'currency',
                          label: t('workflow.builder.properties.numberFormatCurrency'),
                        },
                        {
                          value: 'percentage',
                          label: t('workflow.builder.properties.numberFormatPercentage'),
                        },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name="integerOnly" valuePropName="checked" style={COMPACT_ITEM}>
                    <Checkbox>{t('workflow.builder.properties.integerOnly')}</Checkbox>
                  </Form.Item>
                </>
              )}
            </CollapsibleSection>
          )}

          {/* ── Pre-fill ─────────────────────────────────────────────────────
            Advisor-session value + cross-step source. Every input type except
            RepeatableRow (layout blocks are gated out entirely). */}
          {!isLayoutBlockType(field.type) && field.type !== 'RepeatableRow' && (
            <CollapsibleSection
              id="prefill"
              title={t('workflow.builder.properties.sectionPrefill')}
            >
              {/* Advisor-session prefill — ShortText / LongText only. Allow-list
                mirrors the BE + FormShell resolver; empty = no prefill. */}
              {(field.type === 'ShortText' || field.type === 'LongText') && (
                <Form.Item
                  label={t('workflow.builder.properties.sourceValueLabel')}
                  name="sourceValue"
                  style={COMPACT_ITEM}
                  extra={t('workflow.builder.properties.sourceValueHint')}
                >
                  <Select
                    allowClear
                    placeholder={t('workflow.builder.properties.sourceValuePlaceholder')}
                    options={[
                      {
                        value: 'advisor.fullName',
                        label: t('workflow.builder.properties.sourceValueFullName'),
                      },
                      {
                        value: 'advisor.email',
                        label: t('workflow.builder.properties.sourceValueEmail'),
                      },
                      {
                        value: 'advisor.phone',
                        label: t('workflow.builder.properties.sourceValuePhone'),
                      },
                      {
                        value: 'advisor.dealerCode',
                        label: t('workflow.builder.properties.sourceValueDealerCode'),
                      },
                      {
                        value: 'advisor.repCode',
                        label: t('workflow.builder.properties.sourceValueRepCode'),
                      },
                    ]}
                  />
                </Form.Item>
              )}
              {/* Cross-step prefill picker — two-Select cascade: step (only PRIOR
                steps) → field (only inputs on that step, minus this one).
                Clearing either drops both to null via buildProperties. */}
              <SourceFieldPicker allSteps={allSteps} stepId={stepId} fieldId={field.id} />
            </CollapsibleSection>
          )}

          {/* ── Address complete ─────────────────────────────────────────────
            Canada Post AddressComplete toggle (ShortText) + sibling-role tag
            (ShortText street/city/postal/apt, SingleSelect province). The
            Address compound blueprint stamps these automatically; this is the
            hand-built path. */}
          {(field.type === 'ShortText' || field.type === 'SingleSelect') && (
            <CollapsibleSection
              id="address"
              title={t('workflow.builder.properties.sectionAddress')}
            >
              {field.type === 'ShortText' && (
                <Form.Item
                  name="addressAutocomplete"
                  valuePropName="checked"
                  getValueFromEvent={(e: { target?: { checked?: boolean } }) =>
                    e?.target?.checked ? 'canada-post' : undefined
                  }
                  getValueProps={(value: string | undefined | null) => ({
                    checked: value === 'canada-post',
                  })}
                  style={COMPACT_ITEM}
                >
                  <Checkbox>
                    {t('workflow.builder.properties.addressAutocompleteCanadaPost')}
                  </Checkbox>
                </Form.Item>
              )}
              <Form.Item
                name="addressRole"
                label={t('workflow.builder.properties.addressRoleLabel')}
                style={COMPACT_ITEM}
              >
                <Select
                  allowClear
                  placeholder={t('workflow.builder.properties.addressRolePlaceholder')}
                  options={
                    field.type === 'SingleSelect'
                      ? [
                          {
                            value: 'province',
                            label: t('workflow.builder.properties.addressRoleProvince'),
                          },
                        ]
                      : [
                          {
                            value: 'street',
                            label: t('workflow.builder.properties.addressRoleStreet'),
                          },
                          {
                            value: 'city',
                            label: t('workflow.builder.properties.addressRoleCity'),
                          },
                          {
                            value: 'postal',
                            label: t('workflow.builder.properties.addressRolePostal'),
                          },
                          {
                            value: 'apt',
                            label: t('workflow.builder.properties.addressRoleApt'),
                          },
                        ]
                  }
                />
              </Form.Item>
            </CollapsibleSection>
          )}

          {/* ── PDF field mapping ────────────────────────────────────────────
            Read-only chiclets. AcroForm handles are assigned visually (and
            1-to-many, across PDFs) in the PDF Field Mapping tool
            (originator/PdfFieldMappingPage). Not bound to the form: a property
            edit that omits the mappings preserves them at the BE, so this panel
            never round-trips them. Hidden on layout blocks (nothing to map). */}
          {!isLayoutBlockType(field.type) && (
            <CollapsibleSection
              id="pdfMapping"
              title={t('workflow.builder.properties.sectionPdfMapping')}
              hint={t('workflow.builder.properties.pdfMappingSectionHint')}
            >
              {(field.pdfMappings?.length ?? 0) === 0 ? (
                <span style={{ color: '#6b7280', fontSize: 13 }}>
                  {t('workflow.builder.properties.internalNamePlaceholderReadonly')}
                </span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {field.pdfMappings?.map((m, i) => (
                    <Tag key={`${m.pdfEntryId}:${m.acroFieldName}:${i}`} color="green">
                      {m.acroFieldName}
                    </Tag>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* Explicit Save — autosave (the dot in the header) covers keystrokes,
            but authors still want a manual affordance for "did that stick"
            confidence. Flushes any pending debounce so it always persists the
            latest values instead of racing the timer. */}
          <Form.Item style={COMPACT_ITEM}>
            <Button
              type="default"
              icon={<SaveOutlined />}
              loading={autoSaveStatus === 'saving'}
              onClick={() => {
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                  debounceTimerRef.current = null;
                }
                void persistNow();
              }}
              block
            >
              {t('workflow.builder.properties.save')}
            </Button>
          </Form.Item>

          {/* ── Visibility ───────────────────────────────────────────────────
            Plain button (not collapsible), parked just above Delete in the
            action cluster. Always rendered when the field has a known location
            so authors discover the feature, but disabled when there's nothing
            to gate on yet (no earlier dropdown / radio / checkbox / toggle to
            pick as a source) AND no rules are already set. The disabled tooltip
            tells the user what to add first. */}
          {stepId !== undefined &&
            location !== null &&
            (() => {
              const hasEligibleSources = hasEligibleSourceFields(allSteps, {
                kind: 'field',
                ownStepId: stepId,
                ownSectionId: location.sectionId,
                ownRowId: location.rowId,
                ownColumnId: location.columnId,
              });
              const canEdit = hasConditions || hasEligibleSources;
              const button = (
                <Button
                  type="default"
                  icon={
                    <ControlOutlined style={{ color: hasConditions ? '#0179C1' : '#6b7280' }} />
                  }
                  onClick={() => setVisibilityModalOpen(true)}
                  disabled={!canEdit}
                  block
                >
                  {hasConditions
                    ? t('workflow.builder.properties.visibilityEdit', {
                        count: field.conditions.length,
                      })
                    : t('workflow.builder.properties.visibilityAdd')}
                </Button>
              );
              return (
                <Form.Item style={COMPACT_ITEM}>
                  {canEdit ? (
                    button
                  ) : (
                    <Tooltip title={t('workflow.builder.properties.visibilityDisabledNoSources')}>
                      {/* span wrapper lets Tooltip fire on a disabled button */}
                      <span style={{ display: 'block' }}>{button}</span>
                    </Tooltip>
                  )}
                </Form.Item>
              );
            })()}
          {stepId && location && (
            <ConditionsEditorModal
              open={visibilityModalOpen}
              title={t('workflow.builder.properties.visibilityModalTitle')}
              intro={t('workflow.builder.properties.visibilityIntro')}
              initialConditions={field.conditions ?? []}
              initialCombinator={field.conditionCombinator ?? null}
              allSteps={allSteps}
              // Field-level scope: every eligible field in PRIOR steps, plus
              // same-step fields strictly before this one in document order.
              // See ConditionScope docs.
              scope={{
                kind: 'field',
                ownStepId: stepId,
                ownSectionId: location.sectionId,
                ownRowId: location.rowId,
                ownColumnId: location.columnId,
              }}
              saving={savingConditions}
              onCancel={() => setVisibilityModalOpen(false)}
              onSave={async (conditions, combinator) => {
                if (!stepId || !location) {
                  void message.error(t('workflow.builder.properties.visibilitySaveFailed'));
                  return;
                }
                setSavingConditions(true);
                try {
                  if (templateId) {
                    // Real workflow: persist via the workflow API.
                    await workflowsApi.setFieldConditions(
                      templateId,
                      stepId,
                      location.sectionId,
                      location.rowId,
                      location.columnId,
                      { conditions, combinator },
                    );
                  } else if (onSaveFieldConditions) {
                    // Custom element editor: update in-memory sections state.
                    await onSaveFieldConditions(
                      location.sectionId,
                      location.rowId,
                      location.columnId,
                      conditions,
                      combinator,
                    );
                  } else {
                    void message.error(t('workflow.builder.properties.visibilitySaveFailed'));
                    return;
                  }
                  setVisibilityModalOpen(false);
                  await onConditionsChanged();
                } catch (err) {
                  handleApiError(err, t, t('workflow.builder.properties.visibilitySaveFailed'));
                } finally {
                  setSavingConditions(false);
                }
              }}
            />
          )}

          {/* Save button is gone — auto-save handles persistence; the dot in
            the panel header reflects the live state. Delete + Save-as-custom stay.
            Delete goes through a confirm modal — the builder has no undo.
            Save-as-custom is hidden in the standalone custom-Field editor (you're
            already editing a saved custom field there). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 16 }}>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteConfirmOpen(true)}
              block
            >
              {t('workflow.builder.properties.delete')}
            </Button>
            {!hideSaveAsCustom && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Button icon={<SaveOutlined />} onClick={() => setSaveAsCustomOpen(true)} block>
                  {t('workflow.builder.properties.saveAsCustom')}
                </Button>
              </>
            )}
          </div>
        </Form>
      </AutoSaveTriggerContext.Provider>
      {!hideSaveAsCustom && (
        <SaveAsCustomElementModal
          open={saveAsCustomOpen}
          kind="Field"
          saving={savingAsCustom}
          onCancel={() => setSaveAsCustomOpen(false)}
          onSubmit={handleSaveAsCustom}
        />
      )}
      <BmoModal
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        title={t('workflow.builder.properties.deleteConfirmTitle')}
        width={440}
        footer={[
          <Button key="cancel" onClick={() => setDeleteConfirmOpen(false)}>
            {t('workflow.builder.properties.deleteConfirmCancel')}
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            icon={<DeleteOutlined />}
            onClick={() => {
              setDeleteConfirmOpen(false);
              void onRemove();
            }}
          >
            {t('workflow.builder.properties.deleteConfirmOk')}
          </Button>,
        ]}
      >
        {t('workflow.builder.properties.deleteConfirmBody')}
      </BmoModal>
    </aside>
  );
}

/**
 * Authoring section for an Image field. Lives inline in the field-properties
 * panel — the parent only handles upload state and click handlers since the
 * actual image binary work goes through `workflowsApi.uploadHelpImage`.
 *
 * Reads `imageUrl` reactively via Form.useWatch so the thumbnail preview +
 * Upload/Replace label switch in lockstep with the form state — the panel
 * Save commits the URL onto the field.
 */
function ImageAuthoring({
  templateId,
  uploading,
  onUpload,
  onRemove,
}: {
  templateId: string | undefined;
  uploading: boolean;
  onUpload: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const imageUrl = Form.useWatch<string | undefined>('imageUrl');
  const hasImage = !!imageUrl?.trim();

  return (
    <>
      <Form.Item
        label={t('workflow.builder.properties.imageSectionTitle')}
        style={COMPACT_ITEM}
        extra={!hasImage ? t('workflow.builder.properties.imageUploadHint') : undefined}
      >
        {hasImage ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 8,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              background: '#fafafa',
            }}
          >
            <img
              src={imageUrl}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: 140,
                objectFit: 'contain',
                borderRadius: 4,
                background: '#fff',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="small"
                icon={<PictureOutlined />}
                onClick={onUpload}
                disabled={!templateId || uploading}
                loading={uploading}
                style={{ flex: 1 }}
              >
                {t('workflow.builder.properties.imageReplace')}
              </Button>
              <Button size="small" danger onClick={onRemove}>
                {t('workflow.builder.properties.imageRemove')}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            icon={<PictureOutlined />}
            onClick={onUpload}
            disabled={!templateId || uploading}
            loading={uploading}
            block
          >
            {t('workflow.builder.properties.imageUpload')}
          </Button>
        )}
      </Form.Item>
      {/* Hidden Form.Item so imageUrl participates in form state and reaches
          buildProperties on Save. Set via form.setFieldValue from the upload
          handler. */}
      <Form.Item name="imageUrl" hidden>
        <Input />
      </Form.Item>
      <div style={{ display: 'flex', gap: 8, ...COMPACT_ITEM }}>
        <Form.Item
          label={t('workflow.builder.properties.imageWidthSize')}
          name="imageWidthSize"
          style={{ flex: 1, marginBottom: 0 }}
        >
          <Select
            options={[
              { value: 'small', label: t('workflow.builder.properties.imageWidthSmall') },
              { value: 'medium', label: t('workflow.builder.properties.imageWidthMedium') },
              { value: 'full', label: t('workflow.builder.properties.imageWidthFull') },
            ]}
          />
        </Form.Item>
        <Form.Item
          label={t('workflow.builder.properties.imageAlignment')}
          name="imageAlignment"
          style={{ flex: 1, marginBottom: 0 }}
        >
          <Select
            options={[
              { value: 'left', label: t('workflow.builder.properties.imageAlignLeft') },
              { value: 'center', label: t('workflow.builder.properties.imageAlignCenter') },
              { value: 'right', label: t('workflow.builder.properties.imageAlignRight') },
            ]}
          />
        </Form.Item>
      </div>
      <Form.Item
        label={t('workflow.builder.properties.imageCaptionEn')}
        name="captionEn"
        style={COMPACT_ITEM}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t('workflow.builder.properties.imageCaptionFr')}
        name="captionFr"
        style={COMPACT_ITEM}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t('workflow.builder.properties.imageAltEn')}
        name="altEn"
        style={COMPACT_ITEM}
        extra={t('workflow.builder.properties.imageAltHint')}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={t('workflow.builder.properties.imageAltFr')}
        name="altFr"
        style={COMPACT_ITEM}
      >
        <Input />
      </Form.Item>
    </>
  );
}

/**
 * Normalize a value coming out of AntD's ColorPicker (or sitting in the
 * form's initialValues from DDB) into an opaque hex the BE will accept.
 *
 * The BE validator <c>IsValidHexColor</c> only accepts <c>#xxx</c> (4 chars)
 * or <c>#xxxxxx</c> (7 chars). AntD's ColorPicker can emit <c>#xxxxxxxx</c>
 * (9 chars, with alpha) under several scenarios even with `disabledAlpha`:
 *   - the initial value passed in already carried alpha
 *   - certain color-format internals retain the alpha byte
 *
 * Strategy: trim, ensure leading "#", then slice anything past the opaque
 * 7-char form. Empty / unparseable → null so the wire stays clean. The
 * same helper protects every ColorPicker getValueFromEvent AND the
 * buildProperties output so a stale form initialValue can't smuggle alpha
 * through on a save the user didn't even touch.
 */
/**
 * AntD ColorPicker's onChange fires with (color, hex). The `hex` callback
 * param is unreliable across format / disabledAlpha combinations — it can
 * arrive empty, as `rgb(...)`, or with a leading `0x` depending on internal
 * state. The Color object's `.toHexString()` is the canonical opaque-hex
 * output and is stable across versions, so prefer that.
 *
 * Falls back to the hex callback string if (for some reason) the Color
 * object doesn't expose toHexString — and to null only when both fail.
 */
function pickerHex(color: unknown, hex: string): string | null {
  let raw: string | null = null;
  if (color && typeof color === 'object' && 'toHexString' in color) {
    const fn = (color as { toHexString: () => string }).toHexString;
    if (typeof fn === 'function') {
      const result = fn.call(color);
      if (typeof result === 'string' && result) raw = result;
    }
  }
  if (raw === null && typeof hex === 'string' && hex) raw = hex;
  // Sanitize at the boundary too. If toHexString ever surfaces an 8-char
  // alpha hex (rare, but possible if internal state retained alpha despite
  // disabledAlpha), the form value would round-trip through the picker and
  // re-trigger AntD's disabledAlpha warning. toOpaqueHex strips that.
  return toOpaqueHex(raw);
}

function toOpaqueHex(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  // Strict: only accept hex digits (with or without leading "#"). 3 / 6 are
  // opaque shorthand and full; 4 / 8 carry an alpha byte we strip. Anything
  // else (rgb(), hsl(), named colors, partial inputs) collapses to null so
  // the BE's IsValidHexColor never sees an unparseable value. The previous
  // length-only check let "rgb(0,0,0)" through as "#rgb(0,0,0)" because it
  // didn't validate the inner characters.
  const match = raw.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
  if (!match) return null;
  const digits = match[1]!;
  // Strip alpha: 4-digit → 3-digit, 8-digit → 6-digit.
  if (digits.length === 4) return `#${digits.slice(0, 3)}`;
  if (digits.length === 8) return `#${digits.slice(0, 6)}`;
  return `#${digits}`;
}

/** Allow-list of AddressComplete sibling roles. Kept in lockstep with the
 *  BE — `WorkflowField.IsValidAddressRole` rejects anything else. */
const ADDRESS_ROLES = ['street', 'city', 'province', 'postal', 'apt'] as const;
type AddressRoleValue = (typeof ADDRESS_ROLES)[number];

/** Coerce a free-form form-value string to the narrowed wire type. Blanks
 *  + anything outside the allow-list collapse to null so a stray manual
 *  edit can't sneak past the BE validator. */
function pickAddressRole(raw: string | undefined): AddressRoleValue | null {
  const trimmed = raw?.trim();
  return trimmed && (ADDRESS_ROLES as readonly string[]).includes(trimmed)
    ? (trimmed as AddressRoleValue)
    : null;
}

/**
 * Map the form values into the per-type FieldProperties shape the BE expects. Keys
 * not relevant to the type are omitted — the BE rejects extraneous keys via
 * WorkflowField.ValidateProperties, so emptyish controls (undefined number,
 * blank text) collapse to omission here.
 */
function buildProperties(
  type: WorkflowField['type'],
  v: FormValues,
): WorkflowFieldPropertiesPayload {
  const help = buildHelp(type, v);
  const hint = buildHint(type, v);
  const requiredMessage = buildRequiredMessage(type, v);
  // Field-level PDF AcroForm mappings are managed by the visual PDF Field
  // Mapping tool (1-to-many, per PDF) and NOT sent from this panel. The BE's
  // field Edit preserves existing mappings when the payload omits them, so a
  // property edit here never clears them.
  // ReadOnly + SourceValue — layout blocks refuse both at the BE, so the
  // spread is empty for them and every non-layout branch below picks it up
  // via the `...common` line. SourceValue further scopes to text-input types
  // (ShortText / LongText) at the BE — we still send `null` off-type so a
  // stale form value can't smuggle through.
  const readOnlyProps = isLayoutBlockType(type) ? {} : { readOnly: Boolean(v.readOnly) };
  const canSourceValue = type === 'ShortText' || type === 'LongText';
  const sourceValueProps = canSourceValue ? { sourceValue: v.sourceValue?.trim() || null } : {};
  // UniquenessGroup — ShortText only (BE-enforced). Off-type collapses to
  // null so a stale form value can't reach DDB after a type switch.
  const uniquenessProps =
    type === 'ShortText' ? { uniquenessGroup: v.uniquenessGroup?.trim() || null } : {};
  // MinSelections — CheckboxGroup only (BE-enforced). Same off-type collapse.
  const minSelectionsProps =
    type === 'CheckboxGroup'
      ? { minSelections: typeof v.minSelections === 'number' ? v.minSelections : null }
      : {};
  // SourceField picker writes to two form-side keys; emit the nested
  // wire shape when both are populated, else null. Refused on layout
  // blocks + RepeatableRow at the BE; the common bag is only spread into
  // non-layout branches AND we short-circuit RepeatableRow inside its
  // own branch, so this simple guard is enough.
  const sourceFieldStepId = v.sourceFieldStepId?.trim();
  const sourceFieldFieldId = v.sourceFieldFieldId?.trim();
  const sourceFieldProps =
    isLayoutBlockType(type) || type === 'RepeatableRow'
      ? {}
      : sourceFieldStepId && sourceFieldFieldId
        ? {
            sourceField: {
              stepId: sourceFieldStepId,
              fieldId: sourceFieldFieldId,
            },
          }
        : { sourceField: null };
  const common = {
    ...readOnlyProps,
    ...sourceValueProps,
    ...uniquenessProps,
    ...minSelectionsProps,
    ...sourceFieldProps,
  };
  if (type === 'ShortText') {
    // A masked rule (sin/phone/postal) fully owns the length cap and
    // character set, so we null out the originator-facing minLength /
    // maxLength / inputRestriction on the wire. The properties panel hides
    // the controls when one of those rules is picked; this is the matching
    // defense at the payload layer in case the controls were filled in
    // before the rule was chosen.
    const validate = v.validate?.trim() || null;
    const ruleOwnsFormat = validate === 'sin' || validate === 'phone' || validate === 'postal';
    return {
      placeholder: v.placeholder?.trim() || null,
      minLength: ruleOwnsFormat ? null : (v.minLength ?? null),
      maxLength: ruleOwnsFormat ? null : (v.maxLength ?? null),
      // 'any' sentinel collapses to null on the wire so the BE only sees a
      // concrete restriction or nothing — matches the domain's null-vs-enum
      // validation contract.
      inputRestriction: ruleOwnsFormat
        ? null
        : v.inputRestriction && v.inputRestriction !== 'any'
          ? v.inputRestriction.trim()
          : null,
      validate,
      // AddressAutocomplete piggybacks on ShortText. The properties-panel
      // checkbox stores either 'canada-post' or undefined; null'd on wire
      // when off so the domain's allow-list ('canada-post' only) stays
      // strict.
      addressAutocomplete: v.addressAutocomplete === 'canada-post' ? 'canada-post' : null,
      // AddressRole tags this field as one of the AddressComplete sibling
      // slots ('street' / 'city' / 'province' / 'postal' / 'apt') so the
      // runtime can fill it on a Retrieve. The blueprint stamps roles
      // automatically on compound placement; this is the hand-built path.
      addressRole: pickAddressRole(v.addressRole),
      ...buildRegexProps(validate, v),
      ...help,
      ...hint,
      ...requiredMessage,
      ...common,
    };
  }
  if (type === 'LongText') {
    // 'regex' is the only validate rule LongText accepts (sin/email/phone/postal
    // are ShortText-only on the BE). Its pattern + bilingual message ride along.
    const validate = v.validate?.trim() || null;
    return {
      placeholder: v.placeholder?.trim() || null,
      maxLength: v.maxLength ?? null,
      // 'any' sentinel collapses to null on the wire so the BE only sees a
      // concrete restriction or nothing — matches the domain's null-vs-enum
      // validation contract.
      inputRestriction:
        v.inputRestriction && v.inputRestriction !== 'any' ? v.inputRestriction.trim() : null,
      validate,
      ...buildRegexProps(validate, v),
      ...help,
      ...hint,
      ...requiredMessage,
      ...common,
    };
  }
  if (type === 'Number') {
    // Step removed from the authoring UI — the BE still accepts it for
    // back-compat, but we always send null so the wire shape stays minimal.
    // numberFormat trims to a non-empty string or null (BE allow-list:
    // 'currency' / 'percentage').
    return {
      min: v.min ?? null,
      max: v.max ?? null,
      step: null,
      integerOnly: v.integerOnly ?? null,
      numberFormat: v.numberFormat?.trim() || null,
      ...help,
      ...hint,
      ...requiredMessage,
      ...common,
    };
  }
  if (type === 'Date') {
    // Date inputs — Label + Required + Help + Hint + an optional MinAge cap.
    // MinAge is whole years; the BE refuses out-of-range values (0..150) so
    // the FE only needs to pass through.
    return {
      minAge: v.minAge ?? null,
      ...help,
      ...hint,
      ...requiredMessage,
      ...common,
    };
  }
  if (type === 'Toggle' || type === 'Checkbox') {
    // Boolean inputs — only Label + Required apply on the wire.
    // Help bubble + hint both allowed.
    return { ...help, ...hint, ...requiredMessage };
  }
  // StaticText / Paragraph carry an optional fontSize / color / margins /
  // weight so the originator can style custom copy.
  if (type === 'StaticText' || type === 'Paragraph') {
    // 'regular' is the implicit default — collapse to null so the wire stays
    // tidy when the originator hasn't actually picked a weight.
    const fontWeight = v.fontWeight && v.fontWeight !== 'regular' ? v.fontWeight : null;
    return {
      fontSize: v.fontSize ?? null,
      color: toOpaqueHex(v.color),
      marginTop: v.marginTop ?? null,
      marginBottom: v.marginBottom ?? null,
      fontWeight,
    };
  }
  // Divider carries no properties at all.
  if (type === 'Divider') return {};
  // RepeatableRow — only ships the template + the shared InternalName /
  // help / hint props. No options at the outer level (each column inside
  // the template carries its own options if applicable).
  if (type === 'RepeatableRow') {
    return {
      repeatableTemplate: (v.repeatableTemplate ?? []).map((c) => ({
        id: c.id,
        type: c.type,
        labelEn: c.labelEn?.trim() || '',
        labelFr: c.labelFr?.trim() || null,
        required: c.required ?? false,
        maxLength: c.maxLength ?? null,
        // Carry every per-column property the BE supports — the editor UI
        // doesn't surface these yet, but seed/API-built templates can
        // author them and edit/save must not silently strip them.
        placeholder: c.placeholder?.trim() || null,
        // Only Number columns may carry a toggle (the BE refuses it elsewhere),
        // and only when the switch is on. A mode with no total is legitimate —
        // RIF constrains percentages only — so blanks pass through as null
        // rather than being coerced.
        unitToggle:
          c.type === 'Number' && c.unitToggleEnabled
            ? {
                defaultMode: c.unitToggle?.defaultMode ?? 'dollar',
                dollarTargetConstant: c.unitToggle?.dollarTargetConstant ?? null,
                percentTargetConstant: c.unitToggle?.percentTargetConstant ?? null,
              }
            : null,
        options: c.options?.length
          ? c.options
              .map((o) => {
                const labelEn = o.labelEn?.trim() ?? '';
                if (!labelEn) return null;
                return {
                  value: o.value,
                  labelEn,
                  labelFr: o.labelFr?.trim() || null,
                  helpTitleEn: o.helpTitleEn?.trim() || null,
                  helpTitleFr: o.helpTitleFr?.trim() || null,
                  helpBodyEn: o.helpBodyEn?.trim() || null,
                  helpBodyFr: o.helpBodyFr?.trim() || null,
                };
              })
              .filter((o): o is NonNullable<typeof o> => o !== null)
          : undefined,
        validate: c.validate?.trim() || null,
        inputRestriction: c.inputRestriction?.trim() || null,
        internalName: c.internalName?.trim() || null,
        widthPercent: c.widthPercent ?? null,
        helpEn: c.helpEn?.trim() || null,
        helpFr: c.helpFr?.trim() || null,
      })),
      maxRows: v.maxRows ?? null,
      anchorFirstRow: v.anchorFirstRow ?? null,
      // Cross-row sum rule. Emits null when either the column key is empty
      // OR the picked target slot is empty — half-filled configs must not
      // reach DDB, and the domain would refuse them anyway. Numbers pass
      // through the Number-input value directly (the panel's target
      // constant field is InputNumber, so v.sumValidation.targetConstant is
      // already a plain number or undefined).
      sumValidation: v.sumValidationEnabled ? buildSumValidation(v.sumValidation) : null,
      ...help,
      ...hint,
      ...requiredMessage,
      ...common,
    };
  }
  // Notice — tinted callout with bg color + icon. Shares Color / FontWeight /
  // Margins with the text-styling surface but no FontSize.
  if (type === 'Notice') {
    const fontWeight = v.fontWeight && v.fontWeight !== 'regular' ? v.fontWeight : null;
    return {
      backgroundColor: toOpaqueHex(v.backgroundColor),
      icon: v.icon?.trim() || null,
      color: toOpaqueHex(v.color),
      marginTop: v.marginTop ?? null,
      marginBottom: v.marginBottom ?? null,
      fontWeight,
    };
  }
  // Image — uploaded URL + width/alignment + bilingual caption + alt. The
  // URL itself is optional (the originator can drop an Image first and
  // upload later); width/alignment fall back to renderer defaults when null.
  if (type === 'Image') {
    return {
      imageUrl: v.imageUrl?.trim() || null,
      imageWidthSize: v.imageWidthSize?.trim() || null,
      imageAlignment: v.imageAlignment?.trim() || null,
      captionEn: v.captionEn?.trim() || null,
      captionFr: v.captionFr?.trim() || null,
      altEn: v.altEn?.trim() || null,
      altFr: v.altFr?.trim() || null,
    };
  }
  // BulletedList falls through into the shared options branch below — it uses
  // the same structured editor as SingleSelect / RadioGroup / CheckboxGroup
  // and adds a listStyle marker. Any new layout block added in the future
  // needs its own arm above; we deliberately don't catch the rest with a
  // generic `isLayoutBlock` return so an oversight surfaces as a TS narrowing
  // miss (or a noisy options payload at runtime) rather than a silent {}.
  // SingleSelect / RadioGroup / CheckboxGroup / BulletedList — same authoring
  // surface, only the runtime control differs. Drop rows missing an EN label
  // so a half-filled row at the bottom doesn't 400 the BE (which requires
  // labelEn on every option).
  const rawRows = (v.options ?? [])
    .map((o) => {
      const labelEn = o.labelEn?.trim() ?? '';
      if (!labelEn) return null;
      const labelFr = o.labelFr?.trim() ?? '';
      // Per-item help is paired — pre-clear any half-filled bag (mirror of
      // buildHelp at the field level) so a user who typed a title and then
      // deleted the body still saves cleanly instead of 400'ing on the BE's
      // paired-validation check.
      const titleEn = o.helpTitleEn?.trim() || null;
      const titleFr = o.helpTitleFr?.trim() || null;
      const bodyEn = o.helpBodyEn?.trim() || null;
      const bodyFr = o.helpBodyFr?.trim() || null;
      const helpComplete = titleEn !== null && bodyEn !== null;
      // CheckboxGroup mutual-exclusion targets. The BE refuses this list on
      // any non-CheckboxGroup type (RadioGroup / SingleSelect / BulletedList),
      // so collapse to null off-type so a stale form value can't smuggle a
      // rule through. Empty arrays also collapse to null so the wire matches
      // the domain's empty → null normalisation (avoids a `clearsOnSelect: []`
      // ever hitting DDB).
      const clearsRaw = type === 'CheckboxGroup' ? (o.clearsOnSelect ?? []) : [];
      const clearsOnSelect = clearsRaw.length > 0 ? clearsRaw : null;
      // Inline reveal is RadioGroup / CheckboxGroup on the BE side too —
      // collapse any stale value off-type so a copy/paste or type switch can't
      // smuggle a borrow through onto a SingleSelect or BulletedList.
      const revealFieldId =
        type === 'CheckboxGroup' || type === 'RadioGroup'
          ? (o.revealFieldId ?? null) || null
          : null;
      // Card icon — RadioGroup / CheckboxGroup only. Collapsed off-type so a
      // type switch can't smuggle an icon onto a SingleSelect / BulletedList,
      // which the BE refuses. Blank string normalises to null so clearing the
      // picker actually removes the icon rather than sending "".
      const icon =
        type === 'RadioGroup' || type === 'CheckboxGroup' ? o.icon?.trim() || null : null;
      // Per-option AcroForm checkbox mappings are managed by the visual PDF Field
      // Mapping tool (1-to-many, per PDF) and NOT sent from this panel. The BE's
      // field Edit preserves each option's existing mappings (matched by value)
      // when the payload omits them, so a label/help edit never clears them.
      return {
        value: o.value?.trim() ?? '',
        // Always the EN token — the FR input is a read-only mirror.
        valueFr: o.value?.trim() || null,
        labelEn,
        labelFr: labelFr || null,
        helpTitleEn: helpComplete ? titleEn : null,
        helpTitleFr: helpComplete ? titleFr : null,
        helpBodyEn: helpComplete ? bodyEn : null,
        helpBodyFr: helpComplete ? bodyFr : null,
        clearsOnSelect,
        revealFieldId,
        icon,
      };
    })
    .filter(
      (
        o,
      ): o is {
        value: string;
        valueFr: string | null;
        labelEn: string;
        labelFr: string | null;
        helpTitleEn: string | null;
        helpTitleFr: string | null;
        helpBodyEn: string | null;
        helpBodyFr: string | null;
        clearsOnSelect: string[] | null;
        revealFieldId: string | null;
        icon: string | null;
      } => o !== null,
    );
  // BulletedList re-keys values sequentially (item_1, item_2, …) — the Value
  // column is hidden in the editor for that type because the value is just an
  // internal key, not a user-supplied answer. Other types still require a
  // user-entered value, so rows missing one get dropped here.
  const options =
    type === 'BulletedList'
      ? rawRows.map((o, i) => ({ ...o, value: `item_${i + 1}` }))
      : rawRows.filter((o) => o.value);
  // BulletedList additionally carries listStyle ('disc' or 'decimal'). For
  // input-control types this is null so the BE's top-level guard accepts it.
  const listStyle = type === 'BulletedList' ? v.listStyle?.trim() || 'disc' : null;
  // SingleSelect / RadioGroup / CheckboxGroup are input types — carry the
  // optional `blockingValues` (Next disables when the advisor's answer
  // matches). CheckboxGroup additionally carries the AND/OR operator. Their
  // PDF mappings are managed by the mapping tool and preserved by the BE on
  // edit, so nothing mapping-related is sent from here. BulletedList is a
  // layout block; the spread helpers resolve to `{}` so the wire stays clean.
  const blockingSupported =
    type === 'SingleSelect' || type === 'RadioGroup' || type === 'CheckboxGroup';
  const cleanBlockingValues = blockingSupported
    ? (v.blockingValues ?? []).map((x) => x.trim()).filter((x) => x.length > 0)
    : [];
  const blockingValue =
    cleanBlockingValues.length > 0
      ? {
          blockingValues: cleanBlockingValues,
          // The AND/OR operator only rides along for CheckboxGroup; the BE
          // refuses 'and' on single-value types.
          blockingValueOperator:
            type === 'CheckboxGroup' ? (v.blockingValueOperator ?? 'or') : null,
          // Decorations ride along only while a gate is set — the BE refuses
          // them without a blocking value.
          blockContinueAlert: !!v.blockContinueAlert,
          blockContinueMessageEn: v.blockContinueMessageEn?.trim() || null,
          blockContinueMessageFr: v.blockContinueMessageFr?.trim() || null,
        }
      : {
          blockingValues: null,
          blockingValueOperator: null,
          blockContinueAlert: null,
          blockContinueMessageEn: null,
          blockContinueMessageFr: null,
        };
  // SingleSelect can play the Province role in an Address compound — let
  // hand-built Province dropdowns carry the role tag too. Refused on
  // RadioGroup + BulletedList at the BE.
  const addressRole =
    type === 'SingleSelect' ? { addressRole: pickAddressRole(v.addressRole) } : {};
  // Vertical option layout — RadioGroup / CheckboxGroup only. Collapse to
  // null off-type (the BE refuses it elsewhere) so a stale form value from a
  // type switch can't smuggle the flag through.
  const verticalOptions =
    type === 'RadioGroup' || type === 'CheckboxGroup' ? !!v.verticalOptions : null;
  // Card option layout — RadioGroup / CheckboxGroup only, surfaced in the
  // panel as a boolean switch but stored on the shared `displayAs` property
  // ('card' | 'list'). Collapsed to null off-type so a stale form value from
  // a type switch can't smuggle 'card' onto a Notice, whose `displayAs`
  // vocabulary is 'inline' | 'modal' and would fail BE validation.
  const optionCardLayout =
    type === 'RadioGroup' || type === 'CheckboxGroup'
      ? { displayAs: v.optionCardLayout ? 'card' : 'list' }
      : {};
  return {
    options,
    listStyle,
    verticalOptions,
    ...optionCardLayout,
    ...help,
    ...hint,
    ...requiredMessage,
    ...blockingValue,
    ...addressRole,
    ...common,
  };
}

/**
 * Cross-row sum rule payload. Emits null when the config is incomplete —
 * columnKey empty, or the picked mode's target slot empty. That way a
 * half-configured rule can never reach DDB, and the domain would refuse
 * it anyway. Only called from the RepeatableRow branch.
 */
function buildSumValidation(
  sv: FormValues['sumValidation'],
): WorkflowFieldPropertiesPayload['sumValidation'] {
  if (!sv) return null;
  const columnKey = sv.columnKey?.trim();
  if (!columnKey) return null;
  // Optional display captions — trimmed to null so blank inputs stay off the wire.
  const labels = {
    labelEn: sv.labelEn?.trim() || null,
    labelFr: sv.labelFr?.trim() || null,
  };
  if (sv.mode === 'constant') {
    if (typeof sv.targetConstant !== 'number' || !Number.isFinite(sv.targetConstant)) {
      return null;
    }
    return { columnKey, targetFieldId: null, targetConstant: sv.targetConstant, ...labels };
  }
  if (sv.mode === 'field') {
    const targetFieldId = sv.targetFieldId?.trim();
    if (!targetFieldId) return null;
    return { columnKey, targetFieldId, targetConstant: null, ...labels };
  }
  return null;
}

/**
 * Hint payload. Independent EN + FR; trimmed to empty -> null so the wire stays
 * tidy. Layout blocks don't carry a hint (they have no input to attach it to).
 */
function buildHint(
  type: WorkflowField['type'],
  v: FormValues,
): Pick<WorkflowFieldPropertiesPayload, 'hintEn' | 'hintFr'> {
  if (isLayoutBlockType(type)) return {};
  return {
    hintEn: v.hintEn?.trim() || null,
    hintFr: v.hintFr?.trim() || null,
  };
}

/**
 * Required-error override payload. Same EN/FR pattern as Hint — independent
 * halves, trimmed-empty → null. The BE refuses any value on layout blocks
 * (which can't be required). When `required` is false the override is still
 * persisted (so the originator can pre-author copy and flip the toggle
 * later) — the runtime only consults it when required + empty.
 */
function buildRequiredMessage(
  type: WorkflowField['type'],
  v: FormValues,
): Pick<WorkflowFieldPropertiesPayload, 'requiredMessageEn' | 'requiredMessageFr'> {
  if (isLayoutBlockType(type)) return {};
  return {
    requiredMessageEn: v.requiredMessageEn?.trim() || null,
    requiredMessageFr: v.requiredMessageFr?.trim() || null,
  };
}

/**
 * Regex-rule companions (pattern + bilingual error message). Only carried when
 * the chosen `validate` rule is 'regex'; every other rule (or none) collapses
 * all three to null so a stale pattern authored before switching rules never
 * reaches the wire — the BE refuses these unless validate === 'regex'.
 */
function buildRegexProps(
  validate: string | null,
  v: FormValues,
): Pick<WorkflowFieldPropertiesPayload, 'regexPattern' | 'regexMessageEn' | 'regexMessageFr'> {
  if (validate !== 'regex') {
    return { regexPattern: null, regexMessageEn: null, regexMessageFr: null };
  }
  return {
    regexPattern: v.regexPattern?.trim() || null,
    regexMessageEn: v.regexMessageEn?.trim() || null,
    regexMessageFr: v.regexMessageFr?.trim() || null,
  };
}

/**
 * Help bubble payload. The BE refuses an unpaired set (title without body or
 * vice versa) and refuses any help on layout blocks. We pre-clear the bag if
 * either half is missing so a user who typed a title and then deleted the body
 * doesn't get a 400 — saved as "no help" instead.
 */
function buildHelp(
  type: WorkflowField['type'],
  v: FormValues,
): Pick<
  WorkflowFieldPropertiesPayload,
  'helpTitleEn' | 'helpTitleFr' | 'helpBodyEn' | 'helpBodyFr'
> {
  if (isLayoutBlockType(type)) return {};
  const titleEn = v.helpTitleEn?.trim() || '';
  const bodyEn = v.helpBodyEn?.trim() || '';
  if (!titleEn || !bodyEn) {
    return {
      helpTitleEn: null,
      helpTitleFr: null,
      helpBodyEn: null,
      helpBodyFr: null,
    };
  }
  return {
    helpTitleEn: titleEn,
    helpTitleFr: v.helpTitleFr?.trim() || null,
    helpBodyEn: bodyEn,
    helpBodyFr: v.helpBodyFr?.trim() || null,
  };
}
